import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNavigationBlocker } from '../context/NavigationBlockerContext';
import TiptapEditor from '../components/TiptapEditor';
import DocumentOutline, { HeadingItem } from '../components/DocumentOutline';
import DiffView from '../components/DiffView';
import { areTipTapContentsDifferent } from '../utils/tipTapUtils';
import { useContentSource, useUpdateContentSource, useMetadataTypes } from '../hooks/useContentSources';
import { contentSourceApi, sectionApi } from '../services/cmsApi';
import { MetadataType } from '../services/adminApi';
import { Editor } from '@tiptap/react';
import { VisibilityType, VISIBILITY_LABELS, Section, ContentStatus, CONTENT_STATUS_LABELS, CONTENT_STATUS_COLORS, ReviewStatusType, REVIEW_STATUS_LABELS, REVIEW_STATUS_COLORS } from '../types/cms';
import ContentSourceModal from '../components/ContentSourceModal';
import InfoDialog from '../components/InfoDialog';
import FindReplaceDialog from '../components/FindReplaceDialog';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Info, ArrowLeftToLine, ArrowRightToLine, GitCompare, Save, FileSliders, FileText } from 'lucide-react';
import { Panel, PanelResizeHandle, PanelGroup, ImperativePanelHandle } from 'react-resizable-panels';
import PdfViewer from '../components/PdfViewer';
import ProcessChecklist, { EditorChecklistItem, ChecklistItem } from '../components/ProcessChecklist';
import { getAllMetadataTypes } from '../services/adminApi';
import './ContentSourceEditor.css';

// Deep equality check for JSON objects
const deepEqual = (obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;
    
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    
    if (keys1.length !== keys2.length) return false;
    
    for (const key of keys1) {
        if (!keys2.includes(key)) return false;
        if (!deepEqual(obj1[key], obj2[key])) return false;
    }
    
    return true;
};

/**
 * Helper function to sort sections by document order.
 * 
 * How it works:
 * 1. PRIMARY: Uses `document_order` if available - this is the absolute position 
 *    in the document regardless of hierarchy (the source of truth)
 * 2. FALLBACK: Uses `path` if `document_order` is missing/null - parses hierarchical 
 *    path like "1.2.3" and compares numerically part by part
 * 
 * The `path` method works by:
 * - Splitting paths like "1.2.3" into [1, 2, 3]
 * - Comparing each part numerically (1 vs 1, then 2 vs 2, etc.)
 * - Shorter paths come before longer ones at the same prefix
 * 
 * Example: "1" < "1.1" < "1.2" < "2" < "2.1"
 */
const sortSectionsByPath = (sections: Section[]): Section[] => {
    return [...sections].sort((a, b) => {
        // PRIMARY: Use document_order if available (source of truth)
        const orderA = a.document_order;
        const orderB = b.document_order;
        
        if (orderA != null && orderB != null) {
            // Both have document_order - use it directly
            return orderA - orderB;
        }
        
        if (orderA != null && orderB == null) {
            // a has order, b doesn't - a comes first
            return -1;
        }
        
        if (orderA == null && orderB != null) {
            // b has order, a doesn't - b comes first
            return 1;
        }
        
        // FALLBACK: Both are null - use path-based sorting
        const pathA = a.path || '';
        const pathB = b.path || '';
        const partsA = pathA.split('.').map(Number);
        const partsB = pathB.split('.').map(Number);
        
        // Compare path parts numerically
        for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
            const valA = partsA[i] ?? 0;
            const valB = partsB[i] ?? 0;
            if (valA !== valB) return valA - valB;
        }
        
        return 0;
    });
};

const ContentSourceEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { authState } = useAuth();
    const { setShouldBlockNavigation, setBlockMessage } = useNavigationBlocker();
    
    // Get content source data
    const { data: contentSource, isLoading: isLoadingContentSource, refetch: refetchContentSource, updateData: updateContentSourceData } = useContentSource(id || '');
    const updateContentSourceMutation = useUpdateContentSource();
    
    // Get metadata types
    const { data: allMetadataTypes } = useMetadataTypes();
    
    // CRITICAL FIX: Reset ALL state when navigating to prevent wrong content display
    useEffect(() => {
        if (id) {
            console.log(`[ContentSourceEditor] Switching to content source: ${id}`);
            
            // IMMEDIATELY reset all state
            contentInitializedRef.current = false;
            originalContentStringRef.current = null;
            lastContentSourceIdRef.current = null;
            lastIsReviewModeRef.current = null;
            optimisticVersionRef.current = null;
            
            // Clear content
            setEditingContent('');
            
            // Clear sections
            setSections([]);
            setSelectedSection(null);
            setHeadings([]);
            currentHeadingsRef.current = [];
            setSelectedHeadingId(undefined);
            
            // Clear metadata state
            setLocalSectionMetadata({});
            setCustomFields([]);
            
            // Clear any pending saves
            if (autoSaveTimer) {
                clearTimeout(autoSaveTimer);
                setAutoSaveTimer(null);
            }
            if (metadataSaveTimer.current) {
                clearTimeout(metadataSaveTimer.current);
                metadataSaveTimer.current = null;
            }
            
            // Reset save states
            setHasPendingContentSave(false);
            setHasPendingMetadataSave(false);
            setSaveStatus('idle');
            setMetadataSaveStatus('idle');
            
            // Reset view states
            setShowPdfViewer(false);
            setShowDiffView(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]); // Intentionally exclude autoSaveTimer/metadataSaveTimer to avoid loops
    
    // Local state
    const [editingContent, setEditingContent] = useState('');
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [metadataSaveStatus, setMetadataSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null);
    const [headings, setHeadings] = useState<HeadingItem[]>([]);
    const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
    const [selectedHeadingId, setSelectedHeadingId] = useState<string | undefined>();
    
    // Track if content has been initialized to prevent overwriting user edits
    const contentInitializedRef = useRef(false);
    // Store original content string to detect when we're back to original state
    const originalContentStringRef = useRef<string | null>(null);
    // Track last content source ID and review mode to detect mode-only changes
    const lastContentSourceIdRef = useRef<string | null>(null);
    const lastIsReviewModeRef = useRef<boolean | null>(null);
    // Store optimistic version for next save (updated from API response)
    const optimisticVersionRef = useRef<number | null>(null);
    // Track when we're switching views to ignore onChange events during transition
    const isSwitchingViewsRef = useRef(false);
    // Track when we're programmatically scrolling to ignore scroll-based section changes
    const isProgrammaticScrollRef = useRef(false);
    // Track the last heading that was clicked to prevent scroll-based updates from overriding user selection
    const lastClickedHeadingIdRef = useRef<string | null>(null);
    // Track when we're updating sections for metadata-only changes (skip heading re-extraction)
    const isMetadataOnlyUpdateRef = useRef(false);
    // Track pending section switch when saving metadata before switching
    const pendingSectionSwitchRef = useRef<Section | null>(null);
    // Store current headings for comparison to avoid unnecessary re-extraction
    const currentHeadingsRef = useRef<HeadingItem[]>([]);
    // Ref to store handleManualMetadataSave to avoid dependency order issues
    const handleManualMetadataSaveRef = useRef<(() => Promise<void>) | null>(null);
    // Refs to track latest local metadata state for reliable saving
    const localVisibilityRef = useRef<VisibilityType>('public');
    const localRefVisibilityRef = useRef<VisibilityType>('public');
    const localFilterTagsRef = useRef<string[]>([]);
    const localReviewStatusRef = useRef<ReviewStatusType>('none');
    const localSectionMetadataRef = useRef<Record<string, Array<{value: any, edited_by?: string}>>>({});
    const customFieldsRef = useRef<Array<{ key: string; value: string }>>([]);
    
    // Sections state
    const [sections, setSections] = useState<Section[]>([]);
    const [selectedSection, setSelectedSection] = useState<Section | null>(null);
    const [isLoadingSections, setIsLoadingSections] = useState(false);
    const [sectionsError, setSectionsError] = useState<Error | null>(null);
    
    // Metadata editing state
    const [isMetadataExpanded, setIsMetadataExpanded] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [localVisibility, setLocalVisibility] = useState<VisibilityType>('public');
    const [localRefVisibility, setLocalRefVisibility] = useState<VisibilityType>('public');
    const [localFilterTags, setLocalFilterTags] = useState<string[]>([]);
    const [localReviewStatus, setLocalReviewStatus] = useState<ReviewStatusType>('none');
    const [newTag, setNewTag] = useState('');
    const metadataSaveTimer = useRef<NodeJS.Timeout | null>(null);
    const [hasPendingMetadataSave, setHasPendingMetadataSave] = useState(false);
    const [hasPendingContentSave, setHasPendingContentSave] = useState(false);
    
    // Section metadata state
    // localSectionMetadata now stores arrays of values: { fieldName: [{value: any, edited_by: string}, ...] }
    const [localSectionMetadata, setLocalSectionMetadata] = useState<Record<string, Array<{value: any, edited_by?: string}>>>({});
    const [isAdditionalMetadataExpanded, setIsAdditionalMetadataExpanded] = useState(false);
    // Don't track added fields anymore - allow multiple of each type
    const [customFields, setCustomFields] = useState<Array<{ key: string; value: string }>>([]);
    
    // Sidebar collapse state
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem('editor-sidebar-collapsed');
        return saved === 'true';
    });
    const sidebarPanelRef = useRef<ImperativePanelHandle>(null);
    const defaultSidebarSize = useMemo(() => {
        const saved = localStorage.getItem('editor-sidebar-collapsed');
        return saved === 'true' ? 5 : 20;
    }, []); // Only calculate once on mount
    
    // Diff view state
    const [showDiffView, setShowDiffView] = useState(false);
    const [scrollToHeading, setScrollToHeading] = useState<string | undefined>();
    
    // PDF viewer state
    const [showPdfViewer, setShowPdfViewer] = useState(false);
    
    // Conflict dialog state
    const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
    
    // Info dialog state
    const [infoDialog, setInfoDialog] = useState({ isOpen: false, title: '', message: '' });
    
    // Find and replace dialog state
    const [showFindReplaceDialog, setShowFindReplaceDialog] = useState(false);
    
    // Editor checklist state
    const [editorChecklist, setEditorChecklist] = useState<EditorChecklistItem[]>([]);
    const [isLoadingChecklist, setIsLoadingChecklist] = useState(false);
    
    // Combine sections' original content_json for diff view
    const combinedOriginalContent = useMemo(() => {
        if (!sections || sections.length === 0) {
            return null;
        }
        
        // Sort sections by path for correct hierarchical ordering
        const sortedSections = sortSectionsByPath(sections);
        
        // Combine all sections' content_json (original content)
        const originalContent = sortedSections
            .filter(s => s.content_json)
            .map(s => s.content_json)
            .reduce((acc, content) => {
                if (content && content.content && Array.isArray(content.content)) {
                    if (!acc.content) acc.content = [];
                    acc.content.push(...content.content);
                }
                return acc;
            }, { type: 'doc', content: [] } as any);
        
        return originalContent.content && originalContent.content.length > 0 
            ? originalContent 
            : null;
    }, [sections]);
    
    // Combine sections' content_reviewed_json for diff view (reviewed side)
    const combinedReviewedContent = useMemo(() => {
        if (!showDiffView) return null;
        if (!sections || sections.length === 0) {
            return null;
        }
        
        // Sort sections by path for correct hierarchical ordering
        const sortedSections = sortSectionsByPath(sections);
        
        // Combine all sections' content_reviewed_json (reviewed content)
        // For sections without content_reviewed_json, use content_json (no changes)
        const reviewedContent = sortedSections
            .filter(s => s.content_reviewed_json || s.content_json)
            .map(s => s.content_reviewed_json || s.content_json) // Use reviewed if available, otherwise original
            .reduce((acc, content) => {
                if (content && content.content && Array.isArray(content.content)) {
                    if (!acc.content) acc.content = [];
                    acc.content.push(...content.content);
                }
                return acc;
            }, { type: 'doc', content: [] } as any);
        
        return reviewedContent.content && reviewedContent.content.length > 0 
            ? reviewedContent 
            : null;
    }, [sections, showDiffView]);
    
    // Determine if we're in review mode
    // Review mode is enabled for any status besides 'draft' to prevent editing
    const isReviewMode = useMemo(() => {
        if (!contentSource) return false;
        return contentSource.status !== 'draft';
    }, [contentSource]);
    
    
    // Helper function to recursively extract all text from a node and its children
    // This handles cases where text is split across multiple nodes (e.g., special characters)
    const extractTextFromNode = useCallback((node: any): string => {
        if (!node) return '';
        
        // If this is a text node, return its text
        if (node.type === 'text' && node.text) {
            return node.text;
        }
        
        // If this node has content, recursively extract text from all children
        if (node.content && Array.isArray(node.content)) {
            return node.content.map((child: any) => extractTextFromNode(child)).join('');
        }
        
        return '';
    }, []);

    // Helper function to check if a section's content starts with a specific heading
    const sectionStartsWithHeading = useCallback((section: any, headingText: string, headingLevel: number): boolean => {
        if (!section.content_json || !section.content_json.content || !Array.isArray(section.content_json.content)) {
            return false;
        }
        
        const firstNode = section.content_json.content[0];
        if (firstNode && firstNode.type === 'heading') {
            const sectionHeadingLevel = firstNode.attrs?.level || 1;
            const sectionHeadingText = extractTextFromNode(firstNode).trim();
            return sectionHeadingLevel === headingLevel && sectionHeadingText === headingText;
        }
        
        return false;
    }, [extractTextFromNode]);

    // Extract headings from TipTap JSON content and match with sections
    // Uses multiple matching strategies: title matching, content matching, then index-based fallback
    // CRITICAL: Only extracts TOP-LEVEL headings to match server-side splitContentIntoSection logic
    // Helper to compare two heading arrays to see if they're the same
    // Only compares the properties that matter: level, text, and sectionId
    const areHeadingsEqual = useCallback((headings1: HeadingItem[], headings2: HeadingItem[]): boolean => {
        if (headings1.length !== headings2.length) {
            return false;
        }
        
        for (let i = 0; i < headings1.length; i++) {
            const h1 = headings1[i];
            const h2 = headings2[i];
            
            // Compare level, text, and sectionId
            if (h1.level !== h2.level || 
                h1.text !== h2.text || 
                h1.sectionId !== h2.sectionId) {
                return false;
            }
        }
        
        return true;
    }, []);

    const extractHeadings = useCallback((content: Record<string, any>): HeadingItem[] => {
        const extracted: HeadingItem[] = [];
        
        if (!content || !content.content) return extracted;
        
        // Sort sections by path to maintain document order (paths come from database)
        const sortedSections = sections && sections.length > 0 ? sortSectionsByPath(sections) : [];
        
        // Check if first section is a pre-heading section (created for content before first heading)
        // A pre-heading section's content does NOT start with a heading node
        // A real heading section always starts with a heading node
        // This is more robust than checking title === 'Introduction' because:
        // - A user might have a heading titled "Introduction" which is a real heading section
        // - The pre-heading section might be matched to a heading during sync
        let hasPreHeadingSection = false;
        if (sortedSections.length > 0) {
            const firstSection = sortedSections[0];
            const firstSectionContent = firstSection.content_json;
            // Check if the first section's content starts with a heading node
            // If it doesn't, it's a pre-heading section
            if (firstSectionContent && 
                firstSectionContent.content && 
                Array.isArray(firstSectionContent.content) &&
                firstSectionContent.content.length > 0) {
                const firstNode = firstSectionContent.content[0];
                // If first node is NOT a heading, it's a pre-heading section
                hasPreHeadingSection = firstNode.type !== 'heading';
            }
        }
        const sectionOffset = hasPreHeadingSection ? 1 : 0;
        
        // Track which sections have been matched to avoid duplicates
        const matchedSectionIds = new Set<string>();
        
        // CRITICAL: Only extract headings at the TOP LEVEL of content.content
        // This matches the server-side splitContentIntoSection logic which only looks at top-level nodes
        // Headings inside list items or other nested structures should NOT create sections
        // and should NOT be included in the outline
        // The server iterates through contentJson.content directly (line 66 in contentSplitter.ts)
        // and only checks node.type === 'heading' at the top level, not recursively
        content.content.forEach((node: any, index: number) => {
            if (node.type === 'heading') {
                const level = node.attrs?.level || 1;
                // Recursively extract all text from the heading node and its children
                const rawText = extractTextFromNode(node);
                const text = rawText.trim() || 'Untitled';
                // Use extracted.length for both ID and matching to ensure consistency
                const headingIndex = extracted.length;
                const id = `heading-${headingIndex}`;
                
                let sectionId: string | undefined;
                let path: string | undefined;
                let matchedSection: any = null;
                
                // Strategy 1: Match by section title (most reliable for user-created headings)
                const titleMatch = sortedSections.find(s => 
                    !matchedSectionIds.has(s.id) && 
                    s.title === text
                );
                
                if (titleMatch) {
                    matchedSection = titleMatch;
                } else {
                    // Strategy 2: Match by checking if section content starts with this heading
                    // This handles cases where section title might differ slightly from heading text
                    const contentMatch = sortedSections.find(s => 
                        !matchedSectionIds.has(s.id) && 
                        sectionStartsWithHeading(s, text, level)
                    );
                    
                    if (contentMatch) {
                        matchedSection = contentMatch;
                    } else {
                        // Strategy 3: Fall back to index-based matching (for backwards compatibility)
                        // Only use this if title and content matching both failed
                        const sectionIndex = headingIndex + sectionOffset;
                        if (sectionIndex < sortedSections.length) {
                            const indexSection = sortedSections[sectionIndex];
                            // Only use index-based match if this section hasn't been matched yet
                            if (!matchedSectionIds.has(indexSection.id)) {
                                matchedSection = indexSection;
                            }
                        }
                    }
                }
                
                if (matchedSection) {
                    sectionId = matchedSection.id;
                    path = matchedSection.path;
                    matchedSectionIds.add(matchedSection.id);
                }
                
                extracted.push({
                    id,
                    level, // Use actual heading level
                    text,
                    elementId: id,
                    sectionId, // Associated section ID - matched by title/content/index
                    path, // Hierarchical path from database (e.g., "1.2.3")
                });
            }
            // NOTE: We do NOT recursively traverse nested content (like list items)
            // because the server-side splitContentIntoSection only creates sections for top-level headings
        });
        
        return extracted;
    }, [extractTextFromNode, sections, sectionStartsWithHeading]);
    
    // Load sections when content source loads - get ALL sections including nested ones
    useEffect(() => {
        if (contentSource?.id) {
            // Sync optimistic version with contentSource version when it loads
            if (contentSource.version !== undefined) {
                optimisticVersionRef.current = contentSource.version;
            }
            
            setIsLoadingSections(true);
            setSectionsError(null);
            // Fetch with a high depth to get all nested sections
            // Using depth 10 should cover most nested structures
            sectionApi.getByContentSource(contentSource.id, { depth: 10 })
                .then((loadedSections) => {
                    // Sort sections by path to ensure document order
                    const sortedSections = sortSectionsByPath(loadedSections);
                    setSections(sortedSections);
                    setIsLoadingSections(false);
                    setSectionsError(null);
                })
                .catch((error) => {
                    console.error('Error loading sections:', error);
                    setIsLoadingSections(false);
                    setSectionsError(error);
                });
        } else {
            // Reset sections and loading state when content source changes
            setSections([]);
            setIsLoadingSections(false);
            setSectionsError(null);
        }
        // Only depend on contentSource.id - version sync happens inside when id changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentSource?.id]);
    
    // Load content and initialize metadata on mount
    useEffect(() => {
        // ALWAYS verify we're loading the correct content source
        if (!contentSource || contentSource.id !== id) {
            console.log('[ContentSourceEditor] Waiting for correct content source...');
            return; // Wait for correct content source
        }
        
        if (!sections || sections.length === 0) {
            console.log('[ContentSourceEditor] Waiting for sections to load...');
            return; // Wait for sections
        }
        
        // Check if this is a mode-only change (same content source, different review mode)
        const isModeOnlyChange = lastContentSourceIdRef.current === contentSource.id && 
                                 lastIsReviewModeRef.current !== null &&
                                 lastIsReviewModeRef.current !== isReviewMode &&
                                 contentInitializedRef.current &&
                                 editingContent;
        
        if (isModeOnlyChange) {
            console.log('[ContentSourceEditor] Mode-only change detected, preserving editor content');
            // Update refs to reflect current state
            lastContentSourceIdRef.current = contentSource.id;
            lastIsReviewModeRef.current = isReviewMode;
            return; // Preserve current editor content
        }
        
        // Check if already initialized for THIS specific content source
        // We verify by checking if sections have changed (by comparing section IDs and updated_at)
        if (contentInitializedRef.current && editingContent && originalContentStringRef.current) {
            // Verify the content source ID matches
            if (lastContentSourceIdRef.current === contentSource.id && 
                lastIsReviewModeRef.current === isReviewMode) {
                console.log('[ContentSourceEditor] Content already initialized for this source');
                return; // Already initialized correctly
            } else {
                console.log('[ContentSourceEditor] Content source or mode changed, re-initializing...');
                contentInitializedRef.current = false;
            }
        }
        
        console.log(`[ContentSourceEditor] Initializing content for ${contentSource.id}, mode: ${isReviewMode ? 'review' : 'edit'}`);
        
        // Helper function to combine sections into TipTap content
        const combineSectionsContent = (sectionsToCombine: Section[], useReviewed: boolean = false): Record<string, any> | null => {
            if (!sectionsToCombine || sectionsToCombine.length === 0) {
                return null;
            }
            
            // Sort sections by path for correct hierarchical ordering
            const sortedSections = sortSectionsByPath(sectionsToCombine);
            
            const combinedContent: any[] = [];
            
            sortedSections.forEach((section) => {
                // In review mode, prefer content_reviewed_json, fallback to content_json
                // In edit mode, use content_json
                const content = useReviewed 
                    ? (section.content_reviewed_json || section.content_json)
                    : section.content_json;
                
                if (content && content.content && Array.isArray(content.content)) {
                    combinedContent.push(...content.content);
                }
            });
            
            return combinedContent.length > 0 
                ? { type: 'doc', content: combinedContent }
                : null;
        };
        
        if (contentSource && sections.length > 0) {
            // Always use sections - they are the source of truth
            const combinedContent = isReviewMode
                ? combineSectionsContent(sections, true) // Review mode: use reviewed content
                : combineSectionsContent(sections, false); // Edit mode: use original content
            
            if (combinedContent) {
                const contentStr = JSON.stringify(combinedContent);
                setEditingContent(contentStr);
                if (!originalContentStringRef.current) {
                    originalContentStringRef.current = contentStr;
                }
                contentInitializedRef.current = true;
                lastContentSourceIdRef.current = contentSource.id;
                lastIsReviewModeRef.current = isReviewMode;
            } else {
                // No content in sections - use empty document
                const emptyDoc = { type: 'doc', content: [] };
                const contentStr = JSON.stringify(emptyDoc);
                setEditingContent(contentStr);
                if (!originalContentStringRef.current) {
                    originalContentStringRef.current = contentStr;
                }
                contentInitializedRef.current = true;
                lastContentSourceIdRef.current = contentSource.id;
                lastIsReviewModeRef.current = isReviewMode;
            }
        } else if (contentSource) {
            // Sections not loaded yet - wait for them to load
            // Use empty document as placeholder until sections load
            const emptyDoc = { type: 'doc', content: [] };
            const contentStr = JSON.stringify(emptyDoc);
            setEditingContent(contentStr);
            if (!originalContentStringRef.current) {
                originalContentStringRef.current = contentStr;
            }
            contentInitializedRef.current = false; // Mark as not initialized so it re-initializes when sections load
            lastContentSourceIdRef.current = contentSource.id;
            lastIsReviewModeRef.current = isReviewMode;
        }
        
        console.log('[ContentSourceEditor] Content initialized successfully');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentSource, sections, isReviewMode, id]); // Intentionally exclude editingContent to avoid loops
    
    // Initialize editor checklist from process_metadata or fetch defaults
    useEffect(() => {
        if (!contentSource) return;
        
        const initializeChecklist = async () => {
            // Check if process_metadata has editor checklist
            if (contentSource.process_metadata?.editor?.checklist) {
                setEditorChecklist(contentSource.process_metadata.editor.checklist);
                return;
            }
            
            // Fetch defaults from MetadataType
            setIsLoadingChecklist(true);
            try {
                const metadataTypes = await getAllMetadataTypes();
                const editorChecklistType = metadataTypes.find(
                    (type: MetadataType) => type.field_name === 'process_metadata_editor_checklist'
                );
                
                if (editorChecklistType && editorChecklistType.enum_options && Array.isArray(editorChecklistType.enum_options)) {
                    // Initialize all items with checked: false
                    const defaultChecklist = editorChecklistType.enum_options.map((item: any) => ({
                        ...item,
                        checked: false
                    }));
                    setEditorChecklist(defaultChecklist);
                }
            } catch (error) {
                console.error('Error fetching default editor checklist:', error);
            } finally {
                setIsLoadingChecklist(false);
            }
        };
        
        initializeChecklist();
    }, [contentSource]);
    
    // Handle editor checklist update
    const handleEditorChecklistUpdate = useCallback(async (updatedChecklist: ChecklistItem[]) => {
        if (!contentSource) return;
        
        // Type guard to ensure it's EditorChecklistItem[]
        const editorChecklist = updatedChecklist as EditorChecklistItem[];
        
        try {
            const currentProcessMetadata = contentSource.process_metadata || {};
            const updatedProcessMetadata = {
                ...currentProcessMetadata,
                editor: {
                    checklist: editorChecklist
                }
            };
            
            await contentSourceApi.update(contentSource.id, {
                process_metadata: updatedProcessMetadata
            });
            
            // Update local state
            setEditorChecklist(editorChecklist);
            // Update contentSource data
            updateContentSourceData({
                ...contentSource,
                process_metadata: updatedProcessMetadata
            });
        } catch (error) {
            console.error('Error updating editor checklist:', error);
            throw error;
        }
    }, [contentSource, updateContentSourceData]);
    
    // Filter section-specific metadata types
    const sectionMetadataTypes = useMemo(() => {
        if (!allMetadataTypes) return [];
        
        // Filter for section-specific metadata types
        const sectionFields = ['page_number', 'author', 'date', 'source', 'version', 'summary', 'notes', 'references', 'topics'];
        return allMetadataTypes.filter((type: MetadataType) => sectionFields.includes(type.field_name));
    }, [allMetadataTypes]);

    // Helper function to convert metadata from backend format to UI format
    const convertMetadataToUIFormat = useCallback((metadata: any): Record<string, Array<{value: any, edited_by?: string}>> => {
        if (!metadata || typeof metadata !== 'object') return {};
        
        const uiFormat: Record<string, Array<{value: any, edited_by?: string}>> = {};
        
        for (const key in metadata) {
            const value = metadata[key];
            
            // If already in new array format
            if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'value' in value[0]) {
                uiFormat[key] = value;
            } else if (value !== null && value !== undefined && value !== '') {
                // Old format - convert to array with single item
                uiFormat[key] = [{ value: value }];
            }
        }
        
        return uiFormat;
    }, []);

    // Helper function to convert UI format back to backend format
    // Send in new array format so backend can ensure edited_by is set
    const convertMetadataToBackendFormat = useCallback((metadata: Record<string, Array<{value: any, edited_by?: string}>>, customFieldsData?: Array<{ key: string; value: string }>): Record<string, any> => {
        const backendFormat: Record<string, any> = {};
        
        for (const key in metadata) {
            const values = metadata[key];
            if (Array.isArray(values) && values.length > 0) {
                // Send as array in new format - backend will ensure edited_by is set
                backendFormat[key] = values.map(item => ({
                    value: item.value,
                    edited_by: item.edited_by // Will be set by backend if missing
                }));
            }
        }
        
        // Add custom fields if provided
        if (customFieldsData) {
            const customObj: Record<string, string> = {};
            customFieldsData.forEach(field => {
                if (field.key.trim()) {
                    customObj[field.key.trim()] = field.value;
                }
            });
            if (Object.keys(customObj).length > 0) {
                backendFormat.custom = customObj;
            }
        }
        
        return backendFormat;
    }, []);

    // Helper function to convert metadata from backend format (array) to simple object format for ContentSourceModal
    const convertMetadataToSimpleFormat = useCallback((metadata: any): Record<string, any> => {
        if (!metadata || typeof metadata !== 'object') return {};
        
        const simpleFormat: Record<string, any> = {};
        
        for (const key in metadata) {
            if (key === 'custom') {
                // Preserve custom fields as-is
                simpleFormat.custom = metadata.custom;
            } else {
                const value = metadata[key];
                // If in array format, extract first value; otherwise use as-is
                if (Array.isArray(value) && value.length > 0) {
                    if (typeof value[0] === 'object' && 'value' in value[0]) {
                        simpleFormat[key] = value[0].value;
                    } else {
                        simpleFormat[key] = value[0];
                    }
                } else if (value !== null && value !== undefined && value !== '') {
                    simpleFormat[key] = value;
                }
            }
        }
        
        return simpleFormat;
    }, []);

    // Initialize metadata when selected section or contentSource changes
    useEffect(() => {
        if (selectedSection) {
            // Load section metadata
            const visibility = selectedSection.visibility || 'public';
            const refVisibility = selectedSection.ref_visibility || 'public';
            const filterTags = selectedSection.filter_tags || [];
            const reviewStatus = selectedSection.review_status || 'none';
            
            setLocalVisibility(visibility);
            setLocalRefVisibility(refVisibility);
            setLocalFilterTags(filterTags);
            setLocalReviewStatus(reviewStatus);
            
            // Update refs
            localVisibilityRef.current = visibility;
            localRefVisibilityRef.current = refVisibility;
            localFilterTagsRef.current = filterTags;
            localReviewStatusRef.current = reviewStatus;
            
            // Load section-specific metadata and convert to UI format
            const metadata = selectedSection.metadata || {};
            const uiMetadata = convertMetadataToUIFormat(metadata);
            setLocalSectionMetadata(uiMetadata);
            localSectionMetadataRef.current = uiMetadata;
            
            // Load custom fields
            if (metadata.custom && typeof metadata.custom === 'object') {
                const customEntries = Object.entries(metadata.custom).map(([key, value]) => ({
                    key,
                    value: String(value || '')
                }));
                const customFieldsValue = customEntries.length > 0 ? customEntries : [{ key: '', value: '' }];
                setCustomFields(customFieldsValue);
                customFieldsRef.current = customEntries; // Store actual custom fields (without empty placeholder)
            } else {
                setCustomFields([]);
                customFieldsRef.current = [];
            }
            
            // Auto-expand metadata panel when section is selected
            // setIsMetadataExpanded(true);
        } else if (contentSource) {
            // Load content source metadata
            const visibility = contentSource.visibility || 'public';
            const refVisibility = contentSource.ref_visibility || 'public';
            const filterTags = contentSource.filter_tags || [];
            const reviewStatus = contentSource.review_status || 'none';
            
            setLocalVisibility(visibility);
            setLocalRefVisibility(refVisibility);
            setLocalFilterTags(filterTags);
            setLocalReviewStatus(reviewStatus);
            
            // Update refs
            localVisibilityRef.current = visibility;
            localRefVisibilityRef.current = refVisibility;
            localFilterTagsRef.current = filterTags;
            localReviewStatusRef.current = reviewStatus;
            
            // Clear section metadata when switching to content source
            setLocalSectionMetadata({});
            localSectionMetadataRef.current = {};
            setCustomFields([]);
            customFieldsRef.current = []; // Reset ref to prevent stale custom fields from being used
        }
    }, [selectedSection, contentSource, convertMetadataToUIFormat]);
    
    // Update headings when content changes
    // Skip extraction if this is a metadata-only update (extractHeadings reference changed but content hasn't)
    // Only extract and update if headings have actually changed
    useEffect(() => {
        // Skip heading extraction if this is a metadata-only update
        if (isMetadataOnlyUpdateRef.current) {
            // Reset flag after a short delay to ensure both useEffects have seen it
            setTimeout(() => {
                isMetadataOnlyUpdateRef.current = false;
            }, 0);
            return;
        }
        
        if (editingContent) {
            // Extract headings first to compare with current headings
            try {
                const contentJson = JSON.parse(editingContent);
                const extractedHeadings = extractHeadings(contentJson);
                
                // Compare with ref to avoid dependency loop
                // Only update if headings have actually changed
                if (!areHeadingsEqual(currentHeadingsRef.current, extractedHeadings)) {
                    // Use requestAnimationFrame to allow UI to update
                    // Then use setTimeout to yield to browser during extraction
                    requestAnimationFrame(() => {
                        setTimeout(() => {
                            setHeadings(extractedHeadings);
                            currentHeadingsRef.current = extractedHeadings;
                        }, 0);
                    });
                }
                // If headings haven't changed, do nothing - don't set loading state
            } catch (e) {
                console.error('Error parsing content:', e);
            }
        } else {
            // Only update if headings array is not already empty
            if (currentHeadingsRef.current.length > 0) {
                setHeadings([]);
                currentHeadingsRef.current = [];
            }
        }
    }, [editingContent, extractHeadings, areHeadingsEqual]);
    
    // Re-extract headings when sections change (e.g., after saving a new section)
    // This ensures headings get updated sectionIds when sections are refetched
    // Skip extraction if this is a metadata-only update (content hasn't changed)
    // Only extract and update if headings have actually changed
    useEffect(() => {
        // Skip heading extraction if this is a metadata-only update
        if (isMetadataOnlyUpdateRef.current) {
            // Reset flag after a short delay to ensure both useEffects have seen it
            setTimeout(() => {
                isMetadataOnlyUpdateRef.current = false;
            }, 0);
            return;
        }
        
        if (editingContent && sections.length > 0) {
            try {
                const contentJson = JSON.parse(editingContent);
                const extractedHeadings = extractHeadings(contentJson);
                
                // Compare with ref to avoid dependency loop
                // Only update if headings have actually changed
                if (!areHeadingsEqual(currentHeadingsRef.current, extractedHeadings)) {
                    // Use requestAnimationFrame to allow UI to update
                    // Then use setTimeout to yield to browser during extraction
                    requestAnimationFrame(() => {
                        setTimeout(() => {
                            setHeadings(extractedHeadings);
                            currentHeadingsRef.current = extractedHeadings;
                        }, 0);
                    });
                }
                // If headings haven't changed, do nothing - don't set loading state
            } catch (e) {
                console.error('Error re-extracting headings after sections update:', e);
            }
        }
    }, [sections, extractHeadings, editingContent, areHeadingsEqual]);
    
    // Compute which sections have review changes
    const sectionsWithChanges = useMemo(() => {
        if (!isReviewMode || !sections) return new Set<string>();
        
        const changedSectionIds = new Set<string>();
        sections.forEach(section => {
            if (areTipTapContentsDifferent(section.content_json, section.content_reviewed_json)) {
                changedSectionIds.add(section.id);
            }
        });
        
        return changedSectionIds;
    }, [sections, isReviewMode]);
    
    // Map headings to sections for change indicators using section IDs only
    const headingChangeMap = useMemo(() => {
        if (!isReviewMode || !sections || headings.length === 0) return new Map<string, boolean>();
        
        const map = new Map<string, boolean>();
        
        headings.forEach((heading) => {
            // Use sectionId from heading - sections are only linked by sectionId, not text
            if (heading.sectionId) {
                const hasChanges = sectionsWithChanges.has(heading.sectionId);
                map.set(heading.id, hasChanges);
            }
        });
        
        return map;
    }, [headings, sections, sectionsWithChanges, isReviewMode]);
    
    
    // Save function that can be called manually or by auto-save
    const performSave = useCallback(async (jsonContent: string, isManual: boolean = false) => {
        if (!contentSource) {
            console.error('[performSave] No content source available');
            return;
        }
        
        console.log(`[performSave] Starting save for content source ${contentSource.id}`);
        setSaveStatus('saving');
            
        try {
            const contentJson = JSON.parse(jsonContent);
            
            // Prepare update data with version for optimistic locking
            // Use 'content' parameter (replaces raw_content) - backend will sync to sections
            // Use optimistic version if available, otherwise fall back to contentSource.version
            const currentVersion = optimisticVersionRef.current ?? contentSource.version;
            const updateData: any = { 
                content: contentJson,
                version: currentVersion // Include version for conflict detection
            };
            
            if (isReviewMode) {
                updateData.is_review_mode = true;
            }
            
            // Save to backend and capture the returned updated ContentSource
            let updatedContentSource: any;
            try {
                updatedContentSource = await contentSourceApi.update(contentSource.id, updateData);
            } catch (error: any) {
                // Handle version conflict
                if (error.response?.status === 409) {
                    console.error('[performSave] Version conflict detected');
                    setSaveStatus('error');
                    setHasPendingContentSave(false);
                    setIsConflictDialogOpen(true);
                    
                    // On conflict, we need to refetch to get the latest state
                    // Save scroll position before refetch
                    let scrollPosition = 0;
                    if (editorInstance) {
                        const editorElement = editorInstance.view.dom.closest('.ProseMirror')?.parentElement;
                        if (editorElement) {
                            scrollPosition = editorElement.scrollTop;
                        }
                    }
                    
                    // Refetch to get latest state
                    await refetchContentSource();
                    try {
                        const updatedSections = await sectionApi.getByContentSource(
                            contentSource.id, 
                            { depth: 10 }
                        );
                        const sortedSections = sortSectionsByPath(updatedSections);
                        setSections(sortedSections);
                        
                        // Restore scroll position after refetch
                        if (editorInstance && scrollPosition > 0) {
                            setTimeout(() => {
                                const editorElement = editorInstance.view.dom.closest('.ProseMirror')?.parentElement;
                                if (editorElement) {
                                    editorElement.scrollTop = scrollPosition;
                                }
                            }, 100);
                        }
                    } catch (sectionError) {
                        console.error('[performSave] Error refetching sections after conflict:', sectionError);
                    }
                    return;
                }
                throw error; // Re-throw other errors
            }
            
            console.log('[performSave] Backend save successful');
            
            // Update optimistic version from API response
            if (updatedContentSource?.version !== undefined) {
                optimisticVersionRef.current = updatedContentSource.version;
            }
            // Only refetch sections if heading count changed (new sections created/deleted)
            // This avoids expensive refetches when just editing existing content
            // IMPORTANT: Only count top-level headings (direct children of contentJson.content)
            // This matches the backend's splitContentIntoSection which only creates sections
            // for headings that are direct children of the top-level content array
            let headingCount = 0;
            const headingNodes: any[] = [];
            try {
                if (contentJson.content && Array.isArray(contentJson.content)) {
                    // Only count headings that are direct children of contentJson.content
                    // Do NOT recursively traverse nested content (like list items, blockquotes, etc.)
                    contentJson.content.forEach((node: any) => {
                        if (node.type === 'heading') {
                            headingCount++;
                            headingNodes.push({
                                level: node.attrs?.level || 'unknown',
                                text: node.content?.[0]?.text || node.content?.[0]?.content?.[0]?.text || '',
                                isTopLevel: true
                            });
                        }
                    });
                }
            } catch (e) {
                console.error('[performSave] Error counting headings:', e);
                // If counting fails, err on the side of caution and refetch
                headingCount = sections.length + 1;
            }
            
            // DEBUG: Log detailed information about heading count vs sections
            console.log('[performSave] Heading count comparison:', {
                headingCount,
                sectionsLength: sections.length,
                headingNodes: headingNodes,
                sections: sections.map(s => ({
                    id: s.id,
                    path: s.path,
                    title: s.title,
                    level: s.level
                })),
                contentJsonKeys: Object.keys(contentJson || {}),
                contentJsonContentLength: contentJson?.content?.length
            });
            
            // Only refetch if heading count changed (new sections created or deleted)
            const headingCountChanged = headingCount !== sections.length;
            
            if (headingCountChanged) {
                console.log('[performSave] ⚠️ Heading count changed - will refetch sections', {
                    headingCount,
                    sectionsLength: sections.length,
                    difference: headingCount - sections.length
                });
            } else {
                console.log('[performSave] ✓ Heading count unchanged - skipping section refetch', {
                    headingCount,
                    sectionsLength: sections.length
                });
            }
            
            if (headingCountChanged) {
                // Heading count changed - sections were likely created/deleted, so refetch
                try {
                    const updatedSections = await sectionApi.getByContentSource(
                        contentSource.id, 
                        { depth: 10 }
                    );
                    const sortedSections = sortSectionsByPath(updatedSections);
                    setSections(sortedSections);
                } catch (sectionError) {
                    console.error('[performSave] Error refetching sections:', sectionError);
                    // Continue even if refetch fails - sections will update on next load
                }
            }
            // If heading count unchanged, skip refetch - existing sections were just updated with new content
            
            setSaveStatus('saved');
            setHasPendingContentSave(false);
            
            // Update original content ref to the saved content
            originalContentStringRef.current = jsonContent;
            
            console.log('[performSave] Save completed successfully');
            
            // Reset to idle after 2 seconds
            setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (error) {
            console.error('[performSave] Save error:', error);
            setSaveStatus('error');
            setHasPendingContentSave(false);
        }
    }, [contentSource, isReviewMode, refetchContentSource, editorInstance, sections]);

    // Manual save function
    const handleManualSave = useCallback(async () => {
        if (!editingContent || !contentSource) return;
        
        // Clear auto-save timer
        if (autoSaveTimer) {
            clearTimeout(autoSaveTimer);
            setAutoSaveTimer(null);
        }
        
        // Don't set status here - performSave will set it to 'saving' when it starts
        // Keep hasPendingContentSave true until save completes
        await performSave(editingContent, true);
    }, [editingContent, contentSource, autoSaveTimer, performSave]);


    // Handle content change with auto-save
    const handleContentChange = useCallback((jsonContent: string) => {
        // Ignore onChange events during view switching to prevent false "unsaved changes" detection
        if (isSwitchingViewsRef.current) {
            return;
        }
        
        // Validate that it's valid JSON
        let newContentJson: any;
        try {
            newContentJson = JSON.parse(jsonContent);
        } catch (e) {
            console.error('Invalid JSON received:', e);
            return;
        }
        
        setEditingContent(jsonContent);
        
        // Compare with original content to determine if there are actual changes
        const hasChanges = (() => {
            if (!originalContentStringRef.current) {
                // No original content stored yet, assume there are changes
                return true;
            }
            
            try {
                const originalJson = JSON.parse(originalContentStringRef.current);
                return !deepEqual(newContentJson, originalJson);
            } catch (e) {
                console.error('Error parsing original content for comparison:', e);
                // If we can't parse original, assume there are changes
                return true;
            }
        })();
        
        if (hasChanges) {
            // Don't set status to 'saving' here - we're not saving yet, just marking as unsaved
            // Only set status to 'saving' when we actually start the save operation
            setHasPendingContentSave(true);
            
            // Clear existing timer
            if (autoSaveTimer) {
                clearTimeout(autoSaveTimer);
                // Note: Don't reset hasPendingContentSave here because we're immediately setting a new timer
            }
            
            // Set new timer for auto-save (5 minutes)
            const timer = setTimeout(async () => {
                setHasPendingContentSave(false);
                if (!contentSource) return;
                await performSave(jsonContent, false);
            }, 300000); // 5 minutes (300000ms)
        
        setAutoSaveTimer(timer);
        } else {
            // Content matches original - no unsaved changes
            setHasPendingContentSave(false);
            
            // Clear existing timer
            if (autoSaveTimer) {
                clearTimeout(autoSaveTimer);
                setAutoSaveTimer(null);
            }
        }
    }, [contentSource, autoSaveTimer, performSave]);
    
    // Handle visible section change from scroll detection
    const handleVisibleSectionChange = useCallback((sectionId: string | null) => {
        // Ignore scroll-based updates during programmatic scrolling (e.g., when user clicks a heading)
        if (isProgrammaticScrollRef.current) {
            return;
        }
        
        // Don't override user's clicked heading selection - if a heading was recently clicked,
        // prevent scroll-based updates from changing the selection away from it
        if (lastClickedHeadingIdRef.current) {
            const clickedHeading = headings.find(h => h.id === lastClickedHeadingIdRef.current);
            if (clickedHeading) {
                // Find the heading that would be selected based on the visible section
                const matchingHeading = sectionId ? headings.find(h => h.sectionId === sectionId) : null;
                
                // If the scroll detection would change the selection away from the clicked heading, block it
                // This prevents scroll detection from overriding user's explicit click
                if (matchingHeading && matchingHeading.id !== lastClickedHeadingIdRef.current) {
                    return;
                }
            }
        }
        
        if (!sectionId) {
            // Don't clear selection when no section is visible - keep current selection
            return;
        }
        
        // Find the heading with matching sectionId
        const matchingHeading = headings.find(h => h.sectionId === sectionId);
        
        if (matchingHeading) {
            // Always update selectedHeadingId to ensure re-render (React will handle the optimization)
            setSelectedHeadingId(prevId => {
                if (prevId !== matchingHeading.id) {
                    return matchingHeading.id;
                }
                return prevId;
            });
            
            // Also update selected section if it changed
            if (matchingHeading.sectionId) {
                const matchingSection = sections.find(s => s.id === matchingHeading.sectionId);
                if (matchingSection) {
                    setSelectedSection(prevSection => {
                        if (prevSection?.id !== matchingSection.id) {
                            return matchingSection;
                        }
                        return prevSection;
                    });
                }
            }
        }
    }, [headings, sections]);
    
    // Handle heading click - scroll to heading in editor and select corresponding section
    const handleHeadingClick = useCallback(async (headingId: string) => {
        // Track the clicked heading to prevent scroll-based updates from overriding user selection
        lastClickedHeadingIdRef.current = headingId;
        
        setSelectedHeadingId(headingId);
        
        // Find heading by ID directly (more reliable than using index)
        const targetHeading = headings.find(h => h.id === headingId);
        
        if (!targetHeading || sections.length === 0) return;
        
        // Use sectionId from heading - sections are only linked by sectionId, not text
        // This is the most reliable way to match headings to sections
        let matchingSection: Section | null = null;
        let sectionId = targetHeading.sectionId;
        
        // If heading doesn't have a sectionId, try to get it from the DOM element
        // This handles the case where a new section was created but headings haven't been updated yet
        if (!sectionId && editorInstance) {
            try {
                const editorDOM = editorInstance.view.dom;
                const allHeadingElements = editorDOM.querySelectorAll('h1, h2, h3, h4, h5, h6');
                
                // Extract heading index from headingId (e.g., "heading-5" -> 5)
                // Use position-based matching instead of text-based to handle duplicate heading text
                const headingIndexMatch = headingId.match(/^heading-(\d+)$/);
                const targetHeadingIndex = headingIndexMatch ? parseInt(headingIndexMatch[1], 10) : -1;
                
                if (targetHeadingIndex >= 0) {
                    // Find top-level headings in document order (matching extractHeadings logic)
                    const topLevelHeadings: HTMLElement[] = [];
                    const headingElementsArray = Array.from(allHeadingElements);
                    
                    for (const element of headingElementsArray) {
                        const headingEl = element as HTMLElement;
                        
                        // Check if this is a top-level heading (not nested in a list item)
                        let parent = headingEl.parentElement;
                        let isTopLevel = true;
                        
                        while (parent && parent !== editorDOM) {
                            if (parent.tagName === 'LI' || parent.tagName === 'UL' || parent.tagName === 'OL') {
                                isTopLevel = false;
                                break;
                            }
                            if (parent.classList.contains('ProseMirror')) {
                                break;
                            }
                            parent = parent.parentElement;
                        }
                        
                        if (isTopLevel) {
                            topLevelHeadings.push(headingEl);
                        }
                    }
                    
                    // Get the heading at the target index position
                    if (targetHeadingIndex < topLevelHeadings.length) {
                        const targetElement = topLevelHeadings[targetHeadingIndex];
                        const domSectionId = targetElement.getAttribute('data-section-id');
                        if (domSectionId) {
                            sectionId = domSectionId;
                        }
                    }
                }
            } catch (e) {
                console.warn('[ContentSourceEditor] Error getting sectionId from DOM:', e);
            }
        }
        
        // Try to find matching section by sectionId (most reliable method)
        if (sectionId) {
            matchingSection = sections.find(s => s.id === sectionId) || null;
            if (matchingSection) {
                // Found by sectionId - this is the correct match
            } else {
                // sectionId exists but section not found - might be stale, try fallback
                console.warn(`[ContentSourceEditor] Section ${sectionId} not found in sections array for heading ${headingId}`);
            }
        }
        
        // Only use text matching as fallback if sectionId matching failed
        if (!matchingSection) {
            const headingText = targetHeading.text.trim();
            // Try to find a section with matching title
            // Prefer sections that haven't been matched to other headings yet
            const sortedSections = sortSectionsByPath(sections);
            const textMatch = sortedSections.find(s => s.title === headingText);
            
            if (textMatch) {
                // Verify this section isn't already matched to a different heading
                // (to avoid conflicts when multiple headings have the same text)
                const alreadyMatched = headings.some(h => 
                    h.id !== headingId && 
                    h.sectionId === textMatch.id
                );
                
                if (!alreadyMatched) {
                    matchingSection = textMatch;
                }
            }
        }
        
        // Check if we're switching to a different section and have unsaved metadata
        const isSwitchingSections = matchingSection && selectedSection && matchingSection.id !== selectedSection.id;
        const hasUnsavedMetadata = hasPendingMetadataSave || 
            (selectedSection && Object.keys(localSectionMetadata).length > 0 && 
             JSON.stringify(localSectionMetadata) !== JSON.stringify(convertMetadataToUIFormat(selectedSection.metadata || {})));
        
        if (isSwitchingSections && hasUnsavedMetadata) {
            // Store the section we want to switch to
            pendingSectionSwitchRef.current = matchingSection;
            // Save metadata first - it will switch to the new section after save completes
            if (handleManualMetadataSaveRef.current) {
                await handleManualMetadataSaveRef.current();
            }
            // After save completes, the section switch will have happened
            // Update sectionId to match the newly switched section for scroll logic
            if (matchingSection) {
                sectionId = matchingSection.id;
            }
        } else {
            // No unsaved metadata or switching to same section - switch immediately
            if (matchingSection) {
                setSelectedSection(matchingSection);
            } else {
        // If still no match, clear selection (will show content source metadata)
            setSelectedSection(null);
            }
        }
        
        // Don't try to access editor when diff view is shown (editor is hidden)
        if (showDiffView) {
            // Scroll to heading in diff view (reviewed side)
            if (targetHeading) {
                setScrollToHeading(targetHeading.text);
            }
            return;
        }
        
        if (!editorInstance) return;
        
        // Set flag to ignore scroll-based section changes during programmatic scroll
        isProgrammaticScrollRef.current = true;
        
        // Find the DOM element for this heading using sectionId (most reliable method)
        // This avoids index mismatches when there are nested headings
        const editorDOM = editorInstance.view.dom;
        let targetElement: HTMLElement | null = null;
        
        if (sectionId) {
            // First try: Find heading by data-section-id attribute (most reliable)
            const headingWithSectionId = editorDOM.querySelector(
                `h1[data-section-id="${sectionId}"], h2[data-section-id="${sectionId}"], h3[data-section-id="${sectionId}"], h4[data-section-id="${sectionId}"], h5[data-section-id="${sectionId}"], h6[data-section-id="${sectionId}"]`
            ) as HTMLElement;
            
            if (headingWithSectionId) {
                targetElement = headingWithSectionId;
            }
        }
        
        // Fallback: If sectionId matching failed, find by position/index instead of text
        // This ensures correct matching even when multiple headings have the same text
        if (!targetElement) {
            // Extract heading index from headingId (e.g., "heading-5" -> 5)
            const headingIndexMatch = headingId.match(/^heading-(\d+)$/);
            const targetHeadingIndex = headingIndexMatch ? parseInt(headingIndexMatch[1], 10) : -1;
            
            if (targetHeadingIndex >= 0) {
                const allHeadingElements = editorDOM.querySelectorAll('h1, h2, h3, h4, h5, h6');
                const headingElementsArray = Array.from(allHeadingElements);
                
                // Find top-level headings in document order (matching extractHeadings logic)
                const topLevelHeadings: HTMLElement[] = [];
                
                for (const element of headingElementsArray) {
                    const headingEl = element as HTMLElement;
                    
                    // Check if this is a top-level heading (not nested in a list item)
                    let parent = headingEl.parentElement;
                    let isTopLevel = true;
                    
                    while (parent && parent !== editorDOM) {
                        if (parent.tagName === 'LI' || parent.tagName === 'UL' || parent.tagName === 'OL') {
                            isTopLevel = false;
                            break;
                        }
                        if (parent.classList.contains('ProseMirror')) {
                            break;
                        }
                        parent = parent.parentElement;
                    }
                    
                    // Only use top-level headings (matching extractHeadings logic)
                    if (isTopLevel) {
                        topLevelHeadings.push(headingEl);
                    }
                }
                
                // Get the heading at the target index position
                if (targetHeadingIndex < topLevelHeadings.length) {
                    targetElement = topLevelHeadings[targetHeadingIndex];
                }
            }
        }
        
        // Scroll to the found element
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Add highlight
            targetElement.style.backgroundColor = '#e8f0fe';
            setTimeout(() => {
                targetElement!.style.backgroundColor = '';
            }, 2000);
            
            // Clear the programmatic scroll flag after scroll completes
            // Smooth scroll typically takes ~500ms, so wait a bit longer to be safe
            setTimeout(() => {
                isProgrammaticScrollRef.current = false;
                // Clear the clicked heading ref after scroll settles to allow normal scroll-based updates
                // Use a longer timeout to ensure scroll detection has settled
                setTimeout(() => {
                    lastClickedHeadingIdRef.current = null;
                }, 2000);
            }, 2000);
        } else {
            // If element not found, clear flag immediately
            console.warn(`[ContentSourceEditor] Could not find heading element for heading "${targetHeading.text}" (sectionId: ${sectionId})`);
            isProgrammaticScrollRef.current = false;
            // Clear clicked heading ref after a short delay
            setTimeout(() => {
                lastClickedHeadingIdRef.current = null;
            }, 2000);
        }
    }, [editorInstance, headings, sections, showDiffView, selectedSection, hasPendingMetadataSave, localSectionMetadata, convertMetadataToUIFormat]);
    
    // Unified debounced save function that saves ALL pending metadata changes
    // This collects all changes from local state and saves them together
    const saveAllPendingMetadata = useCallback(async () => {
        if (!contentSource) return;
        
        // Set metadata save status (separate from content save status)
        setMetadataSaveStatus('saving');
        
        try {
            if (selectedSection) {
                // Collect all pending changes for section
                const updateData: any = {};
                
                // Use refs to get the latest values (always up-to-date, not from closure)
                const latestVisibility = localVisibilityRef.current;
                const latestRefVisibility = localRefVisibilityRef.current;
                const latestFilterTags = localFilterTagsRef.current;
                const latestReviewStatus = localReviewStatusRef.current;
                const latestSectionMetadata = localSectionMetadataRef.current;
                
                // Check basic metadata fields - compare against selectedSection (the baseline)
                if (latestVisibility !== selectedSection.visibility) {
                    updateData.visibility = latestVisibility;
                    console.log('[saveAllPendingMetadata] Detected visibility change:', selectedSection.visibility, '->', latestVisibility);
                }
                if (latestRefVisibility !== selectedSection.ref_visibility) {
                    updateData.ref_visibility = latestRefVisibility;
                    console.log('[saveAllPendingMetadata] Detected ref_visibility change:', selectedSection.ref_visibility, '->', latestRefVisibility);
                }
                if (JSON.stringify(latestFilterTags) !== JSON.stringify(selectedSection.filter_tags)) {
                    updateData.filter_tags = latestFilterTags;
                    console.log('[saveAllPendingMetadata] Detected filter_tags change');
                }
                if (latestReviewStatus !== selectedSection.review_status) {
                    updateData.review_status = latestReviewStatus;
                    console.log('[saveAllPendingMetadata] Detected review_status change:', selectedSection.review_status, '->', latestReviewStatus);
                }
                
                // Check if we have section metadata to save (page_number, author, etc.)
                // Compare against the section's current metadata
                const currentMetadata = selectedSection.metadata || {};
                const currentUIMetadata = convertMetadataToUIFormat(currentMetadata);
                
                // Use ref to get latest custom fields
                const latestCustomFields = customFieldsRef.current;
                
                // Compare section metadata - check if any field values have changed
                let hasSectionMetadataChanges = false;
                const latestKeys = Object.keys(latestSectionMetadata);
                const currentKeys = Object.keys(currentUIMetadata);
                
                // Check if keys are different
                if (latestKeys.length !== currentKeys.length || 
                    !latestKeys.every(key => currentKeys.includes(key)) ||
                    !currentKeys.every(key => latestKeys.includes(key))) {
                    hasSectionMetadataChanges = true;
                    console.log('[saveAllPendingMetadata] Section metadata keys changed');
                } else {
                    // Check if any values have changed
                    for (const key of latestKeys) {
                        const latestValues = latestSectionMetadata[key] || [];
                        const currentValues = currentUIMetadata[key] || [];
                        if (latestValues.length !== currentValues.length) {
                            hasSectionMetadataChanges = true;
                            console.log(`[saveAllPendingMetadata] Section metadata ${key} length changed`);
                            break;
                        }
                        for (let i = 0; i < latestValues.length; i++) {
                            if (latestValues[i]?.value !== currentValues[i]?.value) {
                                hasSectionMetadataChanges = true;
                                console.log(`[saveAllPendingMetadata] Section metadata ${key}[${i}] value changed:`, currentValues[i]?.value, '->', latestValues[i]?.value);
                                break;
                            }
                        }
                        if (hasSectionMetadataChanges) break;
                    }
                }
                
                // Also check custom fields
                const hasCustomFieldsChanges = latestCustomFields.length > 0;
                const currentBackendMetadata = convertMetadataToBackendFormat(currentUIMetadata, []);
                const currentCustomFields = currentBackendMetadata.custom || {};
                const currentCustomFieldsArray = Object.entries(currentCustomFields).map(([key, value]) => ({ key, value: String(value) }));
                const customFieldsChanged = JSON.stringify(latestCustomFields) !== JSON.stringify(currentCustomFieldsArray);
                
                // Always include section metadata if:
                // 1. There are detected changes, OR
                // 2. We have section metadata values (user might have just added them), OR
                // 3. We have custom fields that changed
                const hasSectionMetadataValues = Object.keys(latestSectionMetadata).length > 0;
                const shouldIncludeSectionMetadata = hasSectionMetadataChanges || hasSectionMetadataValues || (hasCustomFieldsChanges && customFieldsChanged);
                
                if (shouldIncludeSectionMetadata) {
                    // Save section metadata (page_number, author, etc.)
                    // Always include customFields in the conversion
                    const backendFormat = convertMetadataToBackendFormat(latestSectionMetadata, latestCustomFields);
                    updateData.metadata = backendFormat;
                    console.log('[saveAllPendingMetadata] Including section metadata in save. Reason:', {
                        hasSectionMetadataChanges,
                        hasSectionMetadataValues,
                        customFieldsChanged,
                        latestSectionMetadata,
                        currentUIMetadata,
                        latestCustomFields
                    });
                } else {
                    console.log('[saveAllPendingMetadata] Skipping section metadata - no changes or values', {
                        latestSectionMetadata,
                        currentUIMetadata,
                        latestCustomFields,
                        currentCustomFieldsArray
                    });
                }
                
                console.log('[saveAllPendingMetadata] Update data keys:', Object.keys(updateData), 'Full data:', updateData);
                
                // Only save if there are actual changes
                if (Object.keys(updateData).length > 0) {
                    let updatedSection: Section | null = null;
                    
                    if (isReviewMode && updateData.review_status !== undefined && Object.keys(updateData).length === 1) {
                        // In review mode, if only review_status changed, use reviewSection endpoint
                        await sectionApi.review(selectedSection.id, { review_status: updateData.review_status });
                        // Refetch to get updated section
                        updatedSection = await sectionApi.getById(selectedSection.id);
                    } else {
                        // Save all changes together
                        const response = await sectionApi.update(selectedSection.id, updateData);
                        // Check if response is a Section object
                        if (response && 'id' in response) {
                            updatedSection = response as Section;
                        } else {
                            // If not, refetch to get the updated section
                            updatedSection = await sectionApi.getById(selectedSection.id);
                        }
                    }
                    
                    if (updatedSection) {
                        setSelectedSection(updatedSection);
                        // Mark as metadata-only update to skip heading re-extraction
                        isMetadataOnlyUpdateRef.current = true;
                        // Update sections list
                        setSections(prevSections => prevSections.map(s => s.id === selectedSection.id ? updatedSection! : s));
                        
                        // Update local metadata with the response
                        const uiMetadata = convertMetadataToUIFormat(updatedSection.metadata || {});
                        setLocalSectionMetadata(uiMetadata);
                        localSectionMetadataRef.current = uiMetadata; // Update ref
                    }
                }
            } else {
                // Save to content source
                const updateData: any = {};
                if (localVisibility !== contentSource.visibility) updateData.visibility = localVisibility;
                if (localRefVisibility !== contentSource.ref_visibility) updateData.ref_visibility = localRefVisibility;
                if (JSON.stringify(localFilterTags) !== JSON.stringify(contentSource.filter_tags)) updateData.filter_tags = localFilterTags;
                if (localReviewStatus !== contentSource.review_status) updateData.review_status = localReviewStatus;
                
                if (Object.keys(updateData).length > 0) {
                    await contentSourceApi.update(contentSource.id, updateData);
                }
            }
            
            setMetadataSaveStatus('saved');
            setHasPendingMetadataSave(false);
            
            // If there's a pending section switch, switch to it now
            if (pendingSectionSwitchRef.current) {
                const pendingSection = pendingSectionSwitchRef.current;
                pendingSectionSwitchRef.current = null;
                setSelectedSection(pendingSection);
            }
            
            setTimeout(() => setMetadataSaveStatus('idle'), 2000);
        } catch (error) {
            console.error('Error saving metadata:', error);
            setMetadataSaveStatus('error');
            setHasPendingMetadataSave(false);
            // Clear pending switch on error
            pendingSectionSwitchRef.current = null;
        }
        // We intentionally use refs (localVisibilityRef, etc.) instead of closure values to avoid stale state
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentSource, selectedSection, customFields, isReviewMode, convertMetadataToBackendFormat, convertMetadataToUIFormat]);
    

    // Manual metadata save function - triggers immediate save bypassing debounce
    // Always uses saveAllPendingMetadata to ensure ALL metadata changes are saved together
    const handleManualMetadataSave = useCallback(async () => {
        if (!contentSource) return;
        
        // Clear metadata save timer
        if (metadataSaveTimer.current) {
            clearTimeout(metadataSaveTimer.current);
            metadataSaveTimer.current = null;
        }
        
        // Always use the unified save function which handles all metadata types
        // This ensures that if user changes both section metadata (page_number) and basic metadata
        // (visibility, ref_visibility), both are saved together
        await saveAllPendingMetadata();
    }, [contentSource, saveAllPendingMetadata]);
    
    // Store handleManualMetadataSave in ref for use in handleHeadingClick
    useEffect(() => {
        handleManualMetadataSaveRef.current = handleManualMetadataSave;
    }, [handleManualMetadataSave]);
    
    // Handle content source metadata update from modal
    const handleUpdateContentSource = useCallback(async (updateData: any) => {
        if (!contentSource) return;
        
        try {
            // Convert metadata to backend format if it exists
            const processedData = { ...updateData };
            if (updateData.metadata && typeof updateData.metadata === 'object') {
                // ContentSourceModal provides metadata in simple object format
                // Convert to UI format first (array format), then to backend format
                const uiMetadata: Record<string, Array<{value: any, edited_by?: string}>> = {};
                const customFields: Array<{ key: string; value: string }> = [];
                
                // Separate predefined metadata fields from custom fields
                // Custom fields are stored in metadata.custom in ContentSourceModal
                const metadataObj = updateData.metadata;
                const customObj = metadataObj.custom || {};
                
                // Process regular metadata fields (convert simple values to array format)
                for (const key in metadataObj) {
                    if (key !== 'custom' && metadataObj[key] !== null && metadataObj[key] !== undefined && metadataObj[key] !== '') {
                        const value = metadataObj[key];
                        // If already in array format, use it; otherwise convert to array
                        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'value' in value[0]) {
                            uiMetadata[key] = value;
                        } else {
                            uiMetadata[key] = [{ value: value }];
                        }
                    }
                }
                
                // Process custom fields
                if (typeof customObj === 'object') {
                    Object.entries(customObj).forEach(([key, value]) => {
                        if (key.trim()) {
                            customFields.push({ key: key.trim(), value: String(value || '') });
                        }
                    });
                }
                
                // Convert to backend format using the same function as section metadata
                processedData.metadata = convertMetadataToBackendFormat(uiMetadata, customFields.length > 0 ? customFields : undefined);
            }
            
            const updatedContentSource = await updateContentSourceMutation.mutateAsync({
                id: contentSource.id,
                data: processedData
            });
            // Update local state with the returned data (no refetch needed)
            if (updatedContentSource) {
                updateContentSourceData(updatedContentSource);
            }
            setShowEditModal(false);
        } catch (error) {
            console.error('Error updating content source:', error);
            throw error; // Re-throw so modal can handle error display
        }
    }, [contentSource, updateContentSourceMutation, convertMetadataToBackendFormat, updateContentSourceData]);
    
    // Handle visibility update
    const handleVisibilityUpdate = useCallback((visibility: VisibilityType) => {
        if (!selectedSection && !contentSource) return;
        
        setLocalVisibility(visibility);
        localVisibilityRef.current = visibility; // Update ref immediately
        
        // Don't set status to 'saving' here - we're not saving yet, just marking as unsaved
        // Only set status to 'saving' when we actually start the save operation
        
        // Clear existing timer
        if (metadataSaveTimer.current) {
            clearTimeout(metadataSaveTimer.current);
        }
        
        // Set new timer for auto-save (longer debounce to allow multiple changes)
        setHasPendingMetadataSave(true);
        metadataSaveTimer.current = setTimeout(async () => {
            await saveAllPendingMetadata();
        }, 5000); // Increased to 3 seconds to allow multiple field changes
    }, [contentSource, selectedSection, saveAllPendingMetadata]);
    
    // Handle ref visibility update
    const handleRefVisibilityUpdate = useCallback((refVisibility: VisibilityType) => {
        if (!selectedSection && !contentSource) return;
        
        setLocalRefVisibility(refVisibility);
        localRefVisibilityRef.current = refVisibility; // Update ref immediately
        
        // Don't set status to 'saving' here - we're not saving yet, just marking as unsaved
        // Only set status to 'saving' when we actually start the save operation
        
        // Clear existing timer
        if (metadataSaveTimer.current) {
            clearTimeout(metadataSaveTimer.current);
        }
        
        // Set new timer for auto-save (longer debounce to allow multiple changes)
        setHasPendingMetadataSave(true);
        metadataSaveTimer.current = setTimeout(async () => {
            await saveAllPendingMetadata();
        }, 5000); // Increased to 3 seconds to allow multiple field changes
    }, [contentSource, selectedSection, saveAllPendingMetadata]);
    
    // Handle filter tags update
    const handleFilterTagsUpdate = useCallback((tags: string[]) => {
        if (!selectedSection && !contentSource) return;
        
        setLocalFilterTags(tags);
        localFilterTagsRef.current = tags; // Update ref immediately
        
        // Don't set status to 'saving' here - we're not saving yet, just marking as unsaved
        // Only set status to 'saving' when we actually start the save operation
        
        // Clear existing timer
        if (metadataSaveTimer.current) {
            clearTimeout(metadataSaveTimer.current);
        }
        
        // Set new timer for auto-save (longer debounce to allow multiple changes)
        setHasPendingMetadataSave(true);
        metadataSaveTimer.current = setTimeout(async () => {
            await saveAllPendingMetadata();
        }, 5000); // Increased to 3 seconds to allow multiple field changes
    }, [contentSource, selectedSection, saveAllPendingMetadata]);
    
    // Handle add tag
    const handleAddTag = useCallback(() => {
        if (!newTag.trim() || localFilterTags.includes(newTag.trim())) {
            setNewTag('');
            return;
        }
        const updatedTags = [...localFilterTags, newTag.trim()];
        handleFilterTagsUpdate(updatedTags);
        setNewTag('');
    }, [newTag, localFilterTags, handleFilterTagsUpdate]);
    
    // Handle remove tag
    const handleRemoveTag = useCallback((tagToRemove: string) => {
        const updatedTags = localFilterTags.filter(tag => tag !== tagToRemove);
        handleFilterTagsUpdate(updatedTags);
    }, [localFilterTags, handleFilterTagsUpdate]);
    
    // Handle review status update
    const handleReviewStatusUpdate = useCallback((reviewStatus: ReviewStatusType) => {
        if (!selectedSection && !contentSource) return;
        
        setLocalReviewStatus(reviewStatus);
        localReviewStatusRef.current = reviewStatus; // Update ref immediately
        
        // Don't set status to 'saving' here - we're not saving yet, just marking as unsaved
        // Only set status to 'saving' when we actually start the save operation
        
        // Clear existing timer
        if (metadataSaveTimer.current) {
            clearTimeout(metadataSaveTimer.current);
        }
        
        // Set new timer for auto-save (longer debounce to allow multiple changes)
        setHasPendingMetadataSave(true);
        metadataSaveTimer.current = setTimeout(async () => {
            await saveAllPendingMetadata();
        }, 5000); // Increased to 3 seconds to allow multiple field changes
    }, [contentSource, selectedSection, saveAllPendingMetadata]);
    
    // Handle section metadata update
    const handleSectionMetadataUpdate = useCallback((metadata: Record<string, Array<{value: any, edited_by?: string}>>) => {
        if (!selectedSection) return;
        
        setLocalSectionMetadata(metadata);
        localSectionMetadataRef.current = metadata; // Update ref immediately
        
        // Don't set status to 'saving' here - we're not saving yet, just marking as unsaved
        // Only set status to 'saving' when we actually start the save operation
        
        // Clear existing timer
        if (metadataSaveTimer.current) {
            clearTimeout(metadataSaveTimer.current);
        }
        
        // Set new timer for auto-save (longer debounce to allow multiple changes)
        setHasPendingMetadataSave(true);
        metadataSaveTimer.current = setTimeout(async () => {
            await saveAllPendingMetadata();
        }, 5000); // Increased to 3 seconds to allow multiple field changes
    }, [selectedSection, saveAllPendingMetadata]);
    
    // Handle section metadata field change - update specific value in array
    const handleSectionMetadataFieldChange = useCallback((fieldName: string, valueIndex: number, value: any) => {
        const updated = { ...localSectionMetadata };
        if (!updated[fieldName]) {
            updated[fieldName] = [];
        }
        if (updated[fieldName][valueIndex]) {
            updated[fieldName][valueIndex] = { ...updated[fieldName][valueIndex], value };
        } else {
            updated[fieldName][valueIndex] = { value };
        }
        handleSectionMetadataUpdate(updated);
    }, [localSectionMetadata, handleSectionMetadataUpdate]);
    
    // Handle remove specific value from metadata field
    const handleRemoveMetadataValue = useCallback((fieldName: string, valueIndex: number) => {
        const updated = { ...localSectionMetadata };
        if (updated[fieldName] && Array.isArray(updated[fieldName])) {
            updated[fieldName] = updated[fieldName].filter((_, index) => index !== valueIndex);
            if (updated[fieldName].length === 0) {
                delete updated[fieldName];
            }
            handleSectionMetadataUpdate(updated);
        }
    }, [localSectionMetadata, handleSectionMetadataUpdate]);
    
    // Handle add metadata field (for page_number which is always visible)
    const handleAddMetadataField = useCallback((fieldName: string) => {
        const updated = { ...localSectionMetadata };
        if (!updated[fieldName]) {
            updated[fieldName] = [{ value: '' }];
        } else {
            updated[fieldName] = [...updated[fieldName], { value: '' }];
        }
        setLocalSectionMetadata(updated);
        setIsAdditionalMetadataExpanded(true);
    }, [localSectionMetadata]);
    
    // Handle custom field change
    const handleCustomFieldChange = useCallback((index: number, key: string, value: string) => {
        setCustomFields(prev => {
            const updated = [...prev];
        updated[index] = { key, value };
            
            // Update ref with actual custom fields (filter out empty placeholders)
            const actualCustomFields = updated.filter(field => field.key.trim());
            customFieldsRef.current = actualCustomFields;
            
            return updated;
        });
        
        // Trigger debounced save to include custom fields and all other metadata
        if (metadataSaveTimer.current) {
            clearTimeout(metadataSaveTimer.current);
        }
        setHasPendingMetadataSave(true);
        metadataSaveTimer.current = setTimeout(async () => {
            await saveAllPendingMetadata();
        }, 5000);
    }, [saveAllPendingMetadata]);
    
    // Handle add custom field
    const handleAddCustomField = useCallback(() => {
        setCustomFields(prev => [...prev, { key: '', value: '' }]);
    }, []);
    
    // Handle remove custom field
    const handleRemoveCustomField = useCallback((index: number) => {
        setCustomFields(prev => {
            const updated = prev.filter((_, i) => i !== index);
            // Update ref with actual custom fields
            const actualCustomFields = updated.filter(field => field.key.trim());
            customFieldsRef.current = actualCustomFields;
            return updated;
        });
        
        // Trigger debounced save
        if (metadataSaveTimer.current) {
            clearTimeout(metadataSaveTimer.current);
        }
        setHasPendingMetadataSave(true);
        metadataSaveTimer.current = setTimeout(async () => {
            await saveAllPendingMetadata();
        }, 5000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // We use customFieldsRef.current instead of customFields to avoid stale closures
    }, [saveAllPendingMetadata]);
    
    // Get field label from field name
    const getFieldLabel = useCallback((fieldName: string): string => {
        const labels: Record<string, string> = {
            'page_number': 'Page Number',
            'author': 'Author',
            'date': 'Date',
            'source': 'Source',
            'version': 'Version',
            'summary': 'Summary',
            'notes': 'Notes',
            'references': 'References',
            'topics': 'Topics'
        };
        return labels[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }, []);
    
    // Handle extracting page numbers from headings and updating section metadata
    const handleExtractPageNumbers = useCallback(async (pageNumbers: Array<{ headingText: string; pageNumber: string; level: number; index: number }>) => {
        if (!contentSource || !sections || sections.length === 0) {
            setInfoDialog({
                isOpen: true,
                title: 'No Sections Found',
                message: 'No sections found. Please save the content first to create sections.'
            });
            return;
        }

        try {
            // Get current content to match headings
            const contentJson = editingContent ? JSON.parse(editingContent) : null;
            if (!contentJson || !contentJson.content) {
                setInfoDialog({
                    isOpen: true,
                    title: 'No Content Found',
                    message: 'No content found. Please ensure content is loaded.'
                });
                return;
            }

            // Sort sections by path to maintain document order
            const sortedSections = sortSectionsByPath(sections);

            // Check if first section is a pre-heading section
            let hasPreHeadingSection = false;
            if (sortedSections.length > 0) {
                const firstSection = sortedSections[0];
                const firstSectionContent = firstSection.content_json;
                if (firstSectionContent && 
                    firstSectionContent.content && 
                    Array.isArray(firstSectionContent.content) &&
                    firstSectionContent.content.length > 0) {
                    const firstNode = firstSectionContent.content[0];
                    hasPreHeadingSection = firstNode.type !== 'heading';
                }
            }
            const sectionOffset = hasPreHeadingSection ? 1 : 0;

            // Track which sections have been matched to avoid duplicates
            const matchedSectionIds = new Set<string>();
            
            // Map page numbers to sections
            const sectionUpdates: Array<{ sectionId: string; pageNumber: string; sectionTitle: string }> = [];
            let headingIndex = 0;

            // Extract headings from content and match with page numbers
            contentJson.content.forEach((node: any) => {
                if (node.type === 'heading') {
                    const level = node.attrs?.level || 1;
                    const rawText = extractTextFromNode(node);
                    const text = rawText.trim() || 'Untitled';
                    
                    // Match page number by index position (not by text) to handle duplicate heading text
                    // The index in pageNumbers corresponds to the headingIndex in the document
                    const pageNumberData = pageNumbers.find(pn => pn.index === headingIndex);

                    if (pageNumberData) {
                        let matchedSection: any = null;
                        const currentHeadingIndex = headingIndex;

                        // Strategy 1: Match by section title
                        const titleMatch = sortedSections.find(s => 
                            !matchedSectionIds.has(s.id) && 
                            s.title === text
                        );

                        if (titleMatch) {
                            matchedSection = titleMatch;
                        } else {
                            // Strategy 2: Match by checking if section content starts with this heading
                            const contentMatch = sortedSections.find(s => 
                                !matchedSectionIds.has(s.id) && 
                                sectionStartsWithHeading(s, text, level)
                            );

                            if (contentMatch) {
                                matchedSection = contentMatch;
                            } else {
                                // Strategy 3: Fall back to index-based matching
                                const sectionIndex = currentHeadingIndex + sectionOffset;
                                if (sectionIndex < sortedSections.length) {
                                    const indexSection = sortedSections[sectionIndex];
                                    if (!matchedSectionIds.has(indexSection.id)) {
                                        matchedSection = indexSection;
                                    }
                                }
                            }
                        }

                        if (matchedSection) {
                            sectionUpdates.push({
                                sectionId: matchedSection.id,
                                pageNumber: pageNumberData.pageNumber,
                                sectionTitle: matchedSection.title
                            });
                            matchedSectionIds.add(matchedSection.id);
                        }
                    }

                    headingIndex++;
                }
            });

            if (sectionUpdates.length === 0) {
                setInfoDialog({
                    isOpen: true,
                    title: 'No Matching Sections',
                    message: 'No matching sections found for the extracted page numbers. Make sure sections are saved and headings match.'
                });
                return;
            }

            // Update each section's metadata
            let successCount = 0;
            let errorCount = 0;

            for (const update of sectionUpdates) {
                try {
                    // Get current section metadata
                    const section = sortedSections.find(s => s.id === update.sectionId);
                    if (!section) continue;

                    const currentMetadata = section.metadata || {};
                    const currentUIMetadata = convertMetadataToUIFormat(currentMetadata);
                    
                    // Update page_number field
                    const updatedMetadata = { ...currentUIMetadata };
                    const existingPageNumbers = updatedMetadata.page_number || [];
                    
                    // Check if this page number already exists
                    const pageNumberExists = existingPageNumbers.some((pn: any) => pn.value === update.pageNumber);
                    
                    if (!pageNumberExists) {
                        // Add the page number
                        updatedMetadata.page_number = [...existingPageNumbers, { value: update.pageNumber }];
                    } else {
                        // Page number already exists, skip
                        continue;
                    }

                    // Convert back to backend format
                    const backendFormat = convertMetadataToBackendFormat(updatedMetadata, []);
                    
                    // Update section
                    await sectionApi.update(update.sectionId, { metadata: backendFormat });
                    successCount++;
                } catch (error) {
                    console.error(`Error updating section ${update.sectionId}:`, error);
                    errorCount++;
                }
            }

            // Refresh sections
            if (successCount > 0) {
                const updatedSections = await sectionApi.getByContentSource(contentSource.id, { depth: 10 });
                const sortedUpdatedSections = sortSectionsByPath(updatedSections);
                setSections(sortedUpdatedSections);
                
                // If the currently selected section was updated, refresh it
                if (selectedSection) {
                    const updatedSection = sortedUpdatedSections.find(s => s.id === selectedSection.id);
                    if (updatedSection) {
                        setSelectedSection(updatedSection);
                        const uiMetadata = convertMetadataToUIFormat(updatedSection.metadata || {});
                        setLocalSectionMetadata(uiMetadata);
                        localSectionMetadataRef.current = uiMetadata;
                    }
                }

                setInfoDialog({
                    isOpen: true,
                    title: 'Page Numbers Updated',
                    message: `Successfully updated ${successCount} section(s) with page numbers.${errorCount > 0 ? ` ${errorCount} error(s) occurred.` : ''}`
                });
            } else {
                setInfoDialog({
                    isOpen: true,
                    title: 'No Updates',
                    message: `No sections were updated.${errorCount > 0 ? ` ${errorCount} error(s) occurred.` : ' All page numbers may already exist.'}`
                });
            }
        } catch (error) {
            console.error('Error extracting and updating page numbers:', error);
            setInfoDialog({
                isOpen: true,
                title: 'Error',
                message: 'Error extracting page numbers. Please check the console for details.'
            });
        }
    }, [contentSource, sections, editingContent, extractTextFromNode, sectionStartsWithHeading, selectedSection, convertMetadataToUIFormat, convertMetadataToBackendFormat]);

    // Get available fields to add (excluding page_number which is always visible)
    // Now we allow multiple of each type, so we don't filter out already added fields
    const availableFieldsToAdd = useMemo(() => {
        return sectionMetadataTypes.filter((type: MetadataType) => 
            type.field_name !== 'page_number'
        );
    }, [sectionMetadataTypes]);
    
    // Compute if navigation should be blocked (save in progress or pending)
    const shouldBlockNavigation = useMemo(() => {
        const isSaving = saveStatus === 'saving' || metadataSaveStatus === 'saving';
        return isSaving || hasPendingContentSave || hasPendingMetadataSave;
    }, [saveStatus, metadataSaveStatus, hasPendingContentSave, hasPendingMetadataSave]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = useMemo(() => {
        return hasPendingContentSave || hasPendingMetadataSave;
    }, [hasPendingContentSave, hasPendingMetadataSave]);

    // Update navigation blocker context when save state changes
    useEffect(() => {
        // Block navigation if saving in progress OR if there are unsaved changes
        const shouldBlock = shouldBlockNavigation || hasUnsavedChanges;
        setShouldBlockNavigation(shouldBlock);
        if (hasUnsavedChanges) {
            setBlockMessage('You have unsaved changes. Please save before navigating away.');
        } else if (shouldBlockNavigation) {
            setBlockMessage('You have unsaved changes that are being saved. Please wait for the save to complete before navigating away.');
        }
    }, [shouldBlockNavigation, hasUnsavedChanges, setShouldBlockNavigation, setBlockMessage]);

    // Block browser navigation when save is in progress or there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (shouldBlockNavigation || hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = hasUnsavedChanges 
                    ? 'You have unsaved changes. Are you sure you want to leave?'
                    : 'You have unsaved changes that are being saved. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [shouldBlockNavigation, hasUnsavedChanges]);

    // Keyboard shortcuts (Ctrl+S or Cmd+S for save, Ctrl+F or Cmd+F for find)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ctrl+F or Cmd+F for find and replace
            if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
                e.preventDefault();
                setShowFindReplaceDialog(true);
                return;
            }
            
            // Ctrl+S or Cmd+S for save
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                // Don't save if already saving
                if (saveStatus === 'saving' || metadataSaveStatus === 'saving') {
                    return;
                }
                
                // If metadata panel is expanded and has pending metadata changes, save metadata only
                if (isMetadataExpanded && hasPendingMetadataSave) {
                    handleManualMetadataSave();
                } 
                // Otherwise, save content if there are pending content changes
                else if (hasPendingContentSave) {
                    handleManualSave();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [hasUnsavedChanges, hasPendingContentSave, hasPendingMetadataSave, isMetadataExpanded, saveStatus, metadataSaveStatus, handleManualSave, handleManualMetadataSave]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (metadataSaveTimer.current) {
                clearTimeout(metadataSaveTimer.current);
            }
            if (autoSaveTimer) {
                clearTimeout(autoSaveTimer);
            }
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, [autoSaveTimer]);
    
    // Save collapsed state to localStorage
    useEffect(() => {
        localStorage.setItem('editor-sidebar-collapsed', isSidebarCollapsed.toString());
    }, [isSidebarCollapsed]);
    
    // Debounced resize handler to avoid glitches during drag
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    
    // Handle sidebar resize to detect collapse state smoothly
    const handlePanelResize = useCallback((size: number) => {
        // Clear any pending timeout
        if (resizeTimeoutRef.current) {
            clearTimeout(resizeTimeoutRef.current);
        }
        
        // Debounce the state update to avoid glitches during drag
        resizeTimeoutRef.current = setTimeout(() => {
            // If size is very small (close to minSize), consider it collapsed
            // Use a threshold slightly above minSize to avoid flickering
            const collapsed = size <= 6; // 6% is close to minSize of 5%
            if (collapsed !== isSidebarCollapsed) {
                setIsSidebarCollapsed(collapsed);
            }
        }, 100); // 100ms debounce
    }, [isSidebarCollapsed]);
    
    // Handle sidebar collapse/expand toggle
    const handleToggleSidebar = useCallback(() => {
        if (sidebarPanelRef.current) {
            if (isSidebarCollapsed) {
                sidebarPanelRef.current.expand();
            } else {
                sidebarPanelRef.current.collapse();
            }
        }
    }, [isSidebarCollapsed]);
    
    // Handle panel collapse/expand callbacks (for button clicks)
    const handlePanelCollapse = useCallback(() => {
        setIsSidebarCollapsed(true);
    }, []);
    
    const handlePanelExpand = useCallback(() => {
        setIsSidebarCollapsed(false);
    }, []);
    
    
    // Handle status change
    const handleStatusChange = useCallback(async (newStatus: ContentStatus) => {
        if (!contentSource) return;
        
        try {
            setSaveStatus('saving');
            const updatedContentSource = await updateContentSourceMutation.mutateAsync({
                id: contentSource.id,
                data: { status: newStatus }
            });
            // Update local state with the returned data (no refetch needed)
            if (updatedContentSource) {
                updateContentSourceData(updatedContentSource);
            }
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (error) {
            console.error('Error updating status:', error);
            setSaveStatus('error');
        }
    }, [contentSource, updateContentSourceMutation, updateContentSourceData]);
    
    // Check if PDF viewer should be available
    const hasPdfPath = useMemo(() => {
        return !!(contentSource?.sarangpur_path);
    }, [contentSource?.sarangpur_path]);
    
    // Get PDF URL
    const pdfUrl = useMemo(() => {
        if (!contentSource?.id) return null;
        return `/api/cms/content-sources/${contentSource.id}/pdf`;
    }, [contentSource?.id]);
    
    // Check if we can mark ready for review
    const canMarkReadyForReview = useMemo(() => {
        if (!authState.user || !contentSource) return false;
        const userRole = authState.user.role;
        // Editors and editor-reviewers can mark ready for review when status is draft
        // Admin and superadmin can always see this button
        if (userRole === 'admin' || userRole === 'superadmin') {
            return contentSource.status === 'draft' || contentSource.status === 'ready_for_review';
        }
        const isOwner = contentSource.created_by_id === authState.user.id;
        const isAssigned = contentSource.assigned_to_id === authState.user.id;
        return (isOwner || isAssigned) && contentSource.status === 'draft' && (userRole === 'editor' || userRole === 'editor-reviewer');
    }, [authState.user, contentSource]);
    
    // Check if we can return to draft from ready_for_review or ethics_review
    const canReturnToDraft = useMemo(() => {
        if (!authState.user || !contentSource) return false;
        const userRole = authState.user.role;
        
        // Must be in ready_for_review or ethics_review status
        if (contentSource.status !== 'ready_for_review' && contentSource.status !== 'ethics_review') return false;
        
        // Admin and superadmin can always return to draft
        if (userRole === 'admin' || userRole === 'superadmin') {
            return true;
        }
        
        // Reviewers can return to draft if they can review it (assigned or created by them)
        if (userRole === 'reviewer') {
            const isOwner = contentSource.created_by_id === authState.user.id;
            const isAssigned = contentSource.assigned_to_id === authState.user.id;
            return isOwner || isAssigned;
        }
        
        // Editors and editor-reviewers can return to draft if they own or are assigned
        if (userRole === 'editor' || userRole === 'editor-reviewer') {
            const isOwner = contentSource.created_by_id === authState.user.id;
            const isAssigned = contentSource.assigned_to_id === authState.user.id;
            return isOwner || isAssigned;
        }
        
        return false;
    }, [authState.user, contentSource]);
    
    const canMarkReviewed = useMemo(() => {
        if (!authState.user || !contentSource) return false;
        const userRole = authState.user.role;
        // Admin and superadmin can mark as ethics reviewed when in ethics_review_in_process
        // Reviewers and editor-reviewers can mark as ethics reviewed when in ethics_review_in_process
        if (userRole === 'admin' || userRole === 'superadmin') {
            return contentSource.status === 'ethics_review_in_process';
        }
        return false;
        // return contentSource.status === 'ethics_review_in_process' && (userRole === 'reviewer' || userRole === 'editor-reviewer');
    }, [authState.user, contentSource]);
    
    // Check if admin/superadmin can mark ready_for_review as ethics_review
    const canMarkEthicsReview = useMemo(() => {
        if (!authState.user || !contentSource) return false;
        const userRole = authState.user.role;
        // Only admins and superadmins can move from ready_for_review to ethics_review
        return (userRole === 'admin' || userRole === 'superadmin') && contentSource.status === 'ready_for_review';
    }, [authState.user, contentSource]);
    
    // Loading state
    if (isLoadingContentSource || !contentSource) {
        return (
            <div className="editor-loading">
                <div className="editor-loading-text">Loading content source...</div>
            </div>
        );
    }
    
    return (
        <div className="editor-container" style={{
            border: isReviewMode ? '3px solid #fbbf24' : 'none',
            position: 'relative'
        }}>
            {isReviewMode && (
                <div className="review-mode-indicator">
                    Review Mode
                </div>
            )}
            {isLoadingSections ? (
                <div className="editor-sections-loading">
                    <div className="editor-sections-loading-spinner"></div>
                    <div className="editor-sections-loading-text">Loading sections...</div>
                </div>
            ) : sectionsError ? (
                <div className="editor-sections-error" style={{
                    padding: '2rem',
                    textAlign: 'center',
                    color: '#dc2626',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    margin: '2rem',
                    width: '100%',
                    height: 'fit-content',
                }}>
                    <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
                        Error Loading Sections
                    </h3>
                    <p style={{ marginBottom: '1rem', color: '#991b1b' }}>
                        {sectionsError.message || 'Failed to load sections. This may be due to a timeout when loading large sections.'}
                    </p>
                    {sectionsError.message?.includes('timeout') && (
                        <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#7f1d1d' }}>
                            The request timed out while loading sections. Please try again or contact support if the issue persists.
                        </p>
                    )}
                    <button
                        onClick={() => {
                            setSectionsError(null);
                            if (contentSource?.id) {
                                setIsLoadingSections(true);
                                sectionApi.getByContentSource(contentSource.id, { depth: 10 })
                                    .then((loadedSections) => {
                                        const sortedSections = sortSectionsByPath(loadedSections);
                                        setSections(sortedSections);
                                        setIsLoadingSections(false);
                                        setSectionsError(null);
                                    })
                                    .catch((error) => {
                                        console.error('Error loading sections:', error);
                                        setIsLoadingSections(false);
                                        setSectionsError(error);
                                    });
                            }
                        }}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                        }}
                    >
                        Retry
                    </button>
                </div>
            ) : (
            <PanelGroup direction="horizontal" style={{ height: '100%', width: '100%' }}>
                {/* Left Sidebar - Document Outline */}
                <Panel
                    ref={sidebarPanelRef}
                    defaultSize={defaultSidebarSize}
                    minSize={5}
                    maxSize={30}
                    collapsible={true}
                    onCollapse={handlePanelCollapse}
                    onExpand={handlePanelExpand}
                    onResize={handlePanelResize}
                    className={`editor-sidebar-left ${isSidebarCollapsed ? 'collapsed' : ''}`}
                >
                <div className="editor-sidebar-header">
                    <div className="editor-sidebar-header-top">
                        <div className="editor-title-container">
                            {!isSidebarCollapsed && (
                                <>
                                    <h2 className="editor-title">
                                        {contentSource.title}
                                    </h2>
                                    <button
                                        className="editor-title-edit-button"
                                        onClick={() => setShowEditModal(true)}
                                        title="Edit content source metadata"
                                    >
                                        ✎
                                    </button>
                                </>
                            )}
                        </div>
                        <button
                            className="editor-sidebar-toggle"
                            onClick={handleToggleSidebar}
                            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            {isSidebarCollapsed ? (
                                <ArrowRightToLine size={16} />
                            ) : (
                                <ArrowLeftToLine size={16} />
                            )}
                        </button>
                    </div>
                    {!isSidebarCollapsed && (
                        <div className="editor-header-badges">
                            <span className={`editor-mode-badge ${
                                isReviewMode ? 'editor-mode-badge-review' : 'editor-mode-badge-edit'
                            }`}>
                                {isReviewMode ? 'Review' : 'Edit'}
                            </span>
                            {(() => {
                                const statusColor = CONTENT_STATUS_COLORS[contentSource.status];
                                return (
                                    <span className={`cms-badge cms-badge-${statusColor|| 'gray'}`}>
                                        {CONTENT_STATUS_LABELS[contentSource.status]}
                                    </span>
                                );
                            })()}
                        </div>
                    )}
                </div>
                
                {!isSidebarCollapsed && (
                    <div className="editor-outline-label">Document outline</div>
                )}
                
                <div className={`editor-section-nav ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                    <DocumentOutline
                        headings={headings}
                        onHeadingClick={handleHeadingClick}
                        selectedHeadingId={selectedHeadingId}
                        isCollapsed={isSidebarCollapsed}
                        headingChangeMap={headingChangeMap}
                        isReviewMode={isReviewMode}
                        isLoading={isLoadingSections || !editingContent}
                    />
                </div>
                
                {/* Collapsible Metadata Section */}
                <div className="editor-metadata-section">
                    {selectedSection ? (
                        <Tooltip.Provider>
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                    <div className="editor-metadata-toggle-container">
                                        <button
                                            className="editor-metadata-toggle"
                                            onClick={() => setIsMetadataExpanded(!isMetadataExpanded)}
                                            title={
                                                selectedSection 
                                                    ? `Section: ${selectedSection.title}` 
                                                    : (isMetadataExpanded ? 'Collapse metadata' : 'Expand metadata')
                                            }
                                        >
                                            <Info className="editor-metadata-info-icon" size={18} />
                                            {!isSidebarCollapsed && (
                                                <>
                                                    <span className="editor-metadata-toggle-text">
                                                        {selectedSection ? `Section: ${selectedSection.title}` : 'Content Source Metadata'}
                                                    </span>
                                                    <span className={`editor-metadata-toggle-icon ${isMetadataExpanded ? 'expanded' : ''}`}>
                                                        ▲
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                        {!isSidebarCollapsed && (
                                            <button
                                                className="editor-metadata-clear-section"
                                                onClick={() => setSelectedSection(null)}
                                                title="Clear section selection (show content source metadata)"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content
                                        className="tooltip-content"
                                        side="right"
                                        sideOffset={5}
                                    >
                                        {selectedSection.title}
                                        <Tooltip.Arrow className="tooltip-arrow" />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        </Tooltip.Provider>
                    ) : (
                        <div className="editor-metadata-toggle-container">
                            <button
                                className="editor-metadata-toggle"
                                onClick={() => setIsMetadataExpanded(!isMetadataExpanded)}
                                title={isMetadataExpanded ? 'Collapse metadata' : 'Expand metadata'}
                            >
                                <Info className="editor-metadata-info-icon" size={18} />
                                {!isSidebarCollapsed && (
                                    <>
                                        <span className="editor-metadata-toggle-text">
                                            Content Source Metadata
                                        </span>
                                        <span className={`editor-metadata-toggle-icon ${isMetadataExpanded ? 'expanded' : ''}`}>
                                            ▲
                                        </span>
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                    
                    {isMetadataExpanded && !isSidebarCollapsed && (
                        <div 
                            className="editor-metadata-content" 
                            style={{ 
                                maxHeight: '400px', 
                                overflowY: 'auto', 
                                paddingRight: '8px',
                                position: 'relative',
                                opacity: metadataSaveStatus === 'saving' ? 0.6 : 1,
                                pointerEvents: metadataSaveStatus === 'saving' ? 'none' : 'auto',
                                transition: 'opacity 0.2s ease-in-out'
                            }}
                        >
                            {metadataSaveStatus === 'saving' && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 10,
                                    borderRadius: '4px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <div className="document-outline-loading-spinner" style={{ width: '24px', height: '24px' }}></div>
                                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Saving metadata...</span>
                                    </div>
                                </div>
                            )}
                            {/* Visibility */}
                            <div className="editor-metadata-field">
                                <label className="editor-metadata-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    Visibility
                                    <Tooltip.Provider>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Info className="cms-modal-info-icon" size={16} />
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content
                                                    className="tooltip-content"
                                                    side="right"
                                                    sideOffset={5}
                                                >
                                                    Controls who can view this content.
                                                    <Tooltip.Arrow className="tooltip-arrow" />
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </Tooltip.Provider>
                                </label>
                                <select
                                    value={localVisibility}
                                    onChange={(e) => handleVisibilityUpdate(e.target.value as VisibilityType)}
                                    className="editor-metadata-select"
                                >
                                    {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            
                            {/* Ref Visibility */}
                            <div className="editor-metadata-field">
                                <label className="editor-metadata-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    Ref Visibility
                                    <Tooltip.Provider>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Info className="cms-modal-info-icon" size={16} />
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content
                                                    className="tooltip-content"
                                                    side="right"
                                                    sideOffset={5}
                                                >
                                                    Controls if the AI will display this reference source.
                                                    <Tooltip.Arrow className="tooltip-arrow" />
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </Tooltip.Provider>
                                </label>
                                <select
                                    value={localRefVisibility}
                                    onChange={(e) => handleRefVisibilityUpdate(e.target.value as VisibilityType)}
                                    className="editor-metadata-select"
                                >
                                    {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            
                            {/* Filter Tags */}
                            <div className="editor-metadata-field">
                                <label className="editor-metadata-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    Filter Tags
                                    <Tooltip.Provider>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Info className="cms-modal-info-icon" size={16} />
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content
                                                    className="tooltip-content"
                                                    side="right"
                                                    sideOffset={5}
                                                >
                                                    Tags used for internal filtering for controversial or sensitive topics.
                                                    <Tooltip.Arrow className="tooltip-arrow" />
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </Tooltip.Provider>
                                </label>
                                {localFilterTags.length > 0 && (
                                    <div className="editor-metadata-tags">
                                        {localFilterTags.map((tag) => (
                                            <span key={tag} className="editor-metadata-tag">
                                                {tag}
                                                <button
                                                    onClick={() => handleRemoveTag(tag)}
                                                    className="editor-metadata-tag-remove"
                                                    title="Remove tag"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="editor-metadata-tag-input-container">
                                    <input
                                        type="text"
                                        value={newTag}
                                        onChange={(e) => setNewTag(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddTag();
                                            }
                                        }}
                                        placeholder="Add tag..."
                                        className="editor-metadata-tag-input"
                                    />
                                    <button
                                        onClick={handleAddTag}
                                        className="editor-metadata-tag-add"
                                        title="Add tag"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            
                            {/* Section Metadata - Only show when section is selected */}
                            {selectedSection && (
                                <>
                                    {/* Page Number - Always visible */}
                                    <div className="editor-metadata-field">
                                        <label className="editor-metadata-label">Page Number</label>
                                        {(localSectionMetadata.page_number && localSectionMetadata.page_number.length > 0 
                                            ? localSectionMetadata.page_number 
                                            : [{ value: '' }]
                                        ).map((item, index) => (
                                            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                                                <input
                                                    type="text"
                                                    value={item.value || ''}
                                                    onChange={(e) => handleSectionMetadataFieldChange('page_number', index, e.target.value)}
                                                    placeholder="Enter page number..."
                                                    className="editor-metadata-input"
                                                    style={{ flex: 1 }}
                                                />
                                                {localSectionMetadata.page_number && localSectionMetadata.page_number.length > 1 && (
                                                    <button
                                                        onClick={() => handleRemoveMetadataValue('page_number', index)}
                                                        className="editor-metadata-field-remove"
                                                        title="Remove value"
                                                        type="button"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Additional Metadata - Collapsible */}
                                    {availableFieldsToAdd.length > 0 || Object.keys(localSectionMetadata).some(key => key !== 'page_number') || customFields.length > 0 ? (
                                        <div className="editor-metadata-field">
                                            <button
                                                className="editor-metadata-toggle-small"
                                                onClick={() => setIsAdditionalMetadataExpanded(!isAdditionalMetadataExpanded)}
                                                type="button"
                                            >
                                                <span className="editor-metadata-toggle-text">
                                                    Metadata Fields
                                                </span>
                                                <span className={`editor-metadata-toggle-icon ${isAdditionalMetadataExpanded ? 'expanded' : ''}`}>
                                                    ▲
                                                </span>
                                            </button>
                                            
                                            {isAdditionalMetadataExpanded && (
                                                <div className="editor-metadata-additional-content">
                                                    {/* Add field dropdown */}
                                                    {availableFieldsToAdd.length > 0 && (
                                                        <div className="editor-metadata-add-field">
                                                            <select
                                                                value=""
                                                                onChange={(e) => {
                                                                    if (e.target.value) {
                                                                        handleAddMetadataField(e.target.value);
                                                                        e.target.value = '';
                                                                    }
                                                                }}
                                                                className="editor-metadata-select"
                                                            >
                                                                <option value="">Add metadata field...</option>
                                                                {availableFieldsToAdd.map((type: MetadataType) => (
                                                                    <option key={type.id} value={type.field_name}>
                                                                        {getFieldLabel(type.field_name)}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Display all fields that have values or are being added */}
                                                    {Object.keys(localSectionMetadata)
                                                        .filter(fieldName => fieldName !== 'page_number')
                                                        .map((fieldName) => {
                                                        const metadataType = sectionMetadataTypes.find((t: MetadataType) => t.field_name === fieldName);
                                                        if (!metadataType) return null;
                                                        
                                                        const isTextarea = ['summary', 'notes', 'references'].includes(fieldName);
                                                        const values = localSectionMetadata[fieldName] || [];
                                                        
                                                        return (
                                                            <div key={fieldName} className="editor-metadata-field">
                                                                <div className="editor-metadata-field-header">
                                                                    <label className="editor-metadata-label">{getFieldLabel(fieldName)}</label>
                                                                </div>
                                                                {values.map((item, valueIndex) => (
                                                                    <div key={valueIndex} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' }}>
                                                                        {metadataType.field_type === 'date' ? (
                                                                            <input
                                                                                type="date"
                                                                                value={item.value || ''}
                                                                                onChange={(e) => handleSectionMetadataFieldChange(fieldName, valueIndex, e.target.value)}
                                                                                className="editor-metadata-input"
                                                                                style={{ flex: 1 }}
                                                                            />
                                                                        ) : isTextarea ? (
                                                                            <textarea
                                                                                value={item.value || ''}
                                                                                onChange={(e) => handleSectionMetadataFieldChange(fieldName, valueIndex, e.target.value)}
                                                                                placeholder={`Enter ${getFieldLabel(fieldName).toLowerCase()}...`}
                                                                                className="editor-metadata-textarea"
                                                                                rows={3}
                                                                                style={{ flex: 1 }}
                                                                            />
                                                                        ) : (
                                                                            <input
                                                                                type="text"
                                                                                value={item.value || ''}
                                                                                onChange={(e) => handleSectionMetadataFieldChange(fieldName, valueIndex, e.target.value)}
                                                                                placeholder={`Enter ${getFieldLabel(fieldName).toLowerCase()}...`}
                                                                                className="editor-metadata-input"
                                                                                style={{ flex: 1 }}
                                                                            />
                                                                        )}
                                                                        <button
                                                                            onClick={() => handleRemoveMetadataValue(fieldName, valueIndex)}
                                                                            className="editor-metadata-field-remove"
                                                                            title="Remove value"
                                                                            type="button"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    })}
                                                    
                                                    {/* Custom Fields */}
                                                    <div className="editor-metadata-field">
                                                        <div className="editor-metadata-field-header">
                                                            <label className="editor-metadata-label">Custom Fields</label>
                                                            <button
                                                                onClick={handleAddCustomField}
                                                                className="editor-metadata-field-add"
                                                                title="Add custom field"
                                                                type="button"
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                        {customFields.map((field, index) => (
                                                            <div key={index} className="editor-metadata-custom-field">
                                                                <input
                                                                    type="text"
                                                                    value={field.key}
                                                                    onChange={(e) => handleCustomFieldChange(index, e.target.value, field.value)}
                                                                    placeholder="Field name..."
                                                                    className="editor-metadata-input editor-metadata-custom-key"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={field.value}
                                                                    onChange={(e) => handleCustomFieldChange(index, field.key, e.target.value)}
                                                                    placeholder="Value..."
                                                                    className="editor-metadata-input editor-metadata-custom-value"
                                                                />
                                                                <button
                                                                    onClick={() => handleRemoveCustomField(index)}
                                                                    className="editor-metadata-field-remove"
                                                                    title="Remove custom field"
                                                                    type="button"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="editor-metadata-field">
                                            <button
                                                className="editor-metadata-toggle-small"
                                                onClick={() => setIsAdditionalMetadataExpanded(!isAdditionalMetadataExpanded)}
                                                type="button"
                                            >
                                                <span className="editor-metadata-toggle-text">
                                                    Add Metadata
                                                </span>
                                                <span className={`editor-metadata-toggle-icon ${isAdditionalMetadataExpanded ? 'expanded' : ''}`}>
                                                    ▲
                                                </span>
                                            </button>
                                            
                                            {isAdditionalMetadataExpanded && availableFieldsToAdd.length > 0 && (
                                                <div className="editor-metadata-additional-content">
                                                    <div className="editor-metadata-add-field">
                                                        <select
                                                            value=""
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    handleAddMetadataField(e.target.value);
                                                                    e.target.value = '';
                                                                }
                                                            }}
                                                            className="editor-metadata-select"
                                                        >
                                                            <option value="">Add metadata field...</option>
                                                            {availableFieldsToAdd.map((type: MetadataType) => (
                                                                <option key={type.id} value={type.field_name}>
                                                                    {getFieldLabel(type.field_name)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                            
                            {/* Review Status - Only show in review mode */}
                            {isReviewMode && (
                                <div className="editor-metadata-field">
                                    <label className="editor-metadata-label">Review Status</label>
                                    <select
                                        value={localReviewStatus}
                                        onChange={(e) => handleReviewStatusUpdate(e.target.value as ReviewStatusType)}
                                        className="editor-metadata-select"
                                        style={{
                                            backgroundColor: localReviewStatus === 'escalate' ? '#fee2e2' : 
                                                           localReviewStatus === 'resolved' ? '#dcfce7' : 
                                                           'white',
                                            borderColor: REVIEW_STATUS_COLORS[localReviewStatus] === 'red' ? '#fca5a5' :
                                                       REVIEW_STATUS_COLORS[localReviewStatus] === 'green' ? '#86efac' :
                                                       '#e5e7eb'
                                        }}
                                    >
                                        {Object.entries(REVIEW_STATUS_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                    {localReviewStatus !== 'none' && (
                                        <div style={{ 
                                            marginTop: '4px', 
                                            fontSize: '0.75rem', 
                                            color: REVIEW_STATUS_COLORS[localReviewStatus] === 'red' ? '#dc2626' :
                                                   REVIEW_STATUS_COLORS[localReviewStatus] === 'green' ? '#16a34a' : '#6b7280'
                                        }}>
                                            {localReviewStatus === 'escalate' && '⚠️ Issues found - needs attention'}
                                            {localReviewStatus === 'resolved' && '✓ Review completed'}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                
                {/* Save Status Indicator */}
                <div className="editor-sidebar-footer">
                    <div className="editor-save-status" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ flex: 1 }}>
                        {/* Show content save status or metadata save status (prioritize content) */}
                        {saveStatus === 'saving' && (
                            <div className="editor-save-saving">
                                <div className="editor-save-spinner"></div>
                                {!isSidebarCollapsed && 'Saving...'}
                            </div>
                        )}
                        {saveStatus !== 'saving' && metadataSaveStatus === 'saving' && (
                            <div className="editor-save-saving" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div className="editor-save-spinner"></div>
                                {!isSidebarCollapsed && 'Saving metadata...'}
                                <FileSliders size={14} />
                            </div>
                        )}
                        {saveStatus !== 'saving' && metadataSaveStatus !== 'saving' && (saveStatus === 'saved' || metadataSaveStatus === 'saved') && (
                            <div className="editor-save-saved">
                                <span className="editor-check">✓</span>
                                {!isSidebarCollapsed && 'Saved'}
                            </div>
                        )}
                        {(saveStatus === 'error' || metadataSaveStatus === 'error') && (
                            <div className="editor-save-error">
                                {!isSidebarCollapsed ? 'Error saving' : '!'}
                            </div>
                        )}
                        {saveStatus === 'idle' && metadataSaveStatus === 'idle' && (
                            <div className="editor-save-idle">
                                    {hasUnsavedChanges ? (
                                        <div className="editor-save-error" style={{ color: '#dc2626' }}>
                                            {!isSidebarCollapsed ? 'Unsaved changes' : '!'}
                                    </div>
                                ) : (
                                        !isSidebarCollapsed ? 'All changes saved' : '✓'
                                )}
                            </div>
                        )}
                        </div>
                        <button
                            onClick={() => {
                                if (isMetadataExpanded && hasPendingMetadataSave) {
                                    handleManualMetadataSave();
                                } else {
                                    handleManualSave();
                                }
                            }}
                            disabled={(saveStatus === 'saving' || metadataSaveStatus === 'saving') || !hasUnsavedChanges}
                            className={`editor-save-button ${((saveStatus === 'saving' || metadataSaveStatus === 'saving') || !hasUnsavedChanges) ? 'disabled' : ''}`}
                            title={
                                isMetadataExpanded && hasPendingMetadataSave
                                    ? "Save metadata (Ctrl+S or Cmd+S)"
                                    : hasUnsavedChanges
                                    ? "Save changes (Ctrl+S or Cmd+S)"
                                    : "No changes to save"
                            }
                        >
                            {isMetadataExpanded && hasPendingMetadataSave ? (
                                <FileSliders size={14} />
                            ) : (
                                <Save size={14} />
                            )}
                        </button>
                    </div>
                </div>
                </Panel>
                
                {/* Resize Handle */}
                <PanelResizeHandle className="editor-sidebar-resizer" />
                
                {/* Main Editor Area - Google Docs Style */}
                {/* Extract common editor content to avoid duplication */}
                {(() => {
                    const editorContent = (
                        <div className="editor-main" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {/* Minimal Header */}
                            <div className="editor-main-header">
                                <div className={`editor-main-title-group ${showPdfViewer ? 'pdf-viewer-active' : ''}`}>
                                    <div className="editor-main-title-container">
                                        <h3 className="editor-main-document-title">{contentSource.title}</h3>
                                    </div>
                                    <div className="editor-status-buttons">
                                        <ProcessChecklist
                                            checklist={editorChecklist}
                                            onUpdate={handleEditorChecklistUpdate}
                                            title="Editor Checklist"
                                            isLoading={isLoadingChecklist}
                                            type="editor"
                                        />
                                        {/* {!showDiffView && (
                                            <button
                                                onClick={() => setShowFindReplaceDialog(true)}
                                                className="editor-header-button editor-header-button-secondary"
                                                title="Find and Replace (Ctrl+F or Cmd+F)"
                                            >
                                                <Search size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                                Find & Replace
                                            </button>
                                        )} */}
                                        {hasPdfPath && (
                                            <button
                                                onClick={() => setShowPdfViewer(!showPdfViewer)}
                                                className={`editor-header-button ${showPdfViewer ? 'editor-header-button-pdf' : 'editor-header-button-pdf-secondary'}`}
                                                title={showPdfViewer ? 'Hide PDF viewer' : 'Show PDF viewer'}
                                            >
                                                <FileText size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                                {showPdfViewer ? 'Hide PDF' : 'Show PDF'}
                                            </button>
                                        )}
                                        {isReviewMode && (
                                            <button
                                                onClick={() => {
                                                    const willShowDiff = !showDiffView;
                                                    // Set flag to ignore onChange during view switch
                                                    isSwitchingViewsRef.current = true;
                                                    setShowDiffView(willShowDiff);
                                                    // Clear flag after a short delay to allow editor to initialize
                                                    setTimeout(() => {
                                                        isSwitchingViewsRef.current = false;
                                                        // Update original content ref to current content when switching back to editor
                                                        // This prevents false "unsaved changes" detection
                                                        if (!willShowDiff && editingContent && originalContentStringRef.current) {
                                                            // Only update if content hasn't actually changed
                                                            try {
                                                                const currentJson = JSON.parse(editingContent);
                                                                const originalJson = JSON.parse(originalContentStringRef.current);
                                                                if (deepEqual(currentJson, originalJson)) {
                                                                    // Content is the same, update ref to prevent false positives
                                                                    originalContentStringRef.current = editingContent;
                                                                    setHasPendingContentSave(false);
                                                                }
                                                            } catch (e) {
                                                                // Ignore parsing errors
                                                            }
                                                        }
                                                    }, 100);
                                                }}
                                                className={`editor-header-button ${showDiffView ? 'editor-header-button-active' : 'editor-header-button-secondary'}`}
                                                title={showDiffView ? 'Hide diff view' : 'Show diff view'}
                                            >
                                                <GitCompare size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                                {showDiffView ? 'Hide Diff' : 'Show Diff'}
                                            </button>
                                        )}
                                        {canReturnToDraft && (
                                            <button
                                                onClick={() => handleStatusChange('draft')}
                                                className="editor-header-button editor-header-button-secondary"
                                            >
                                                Return to Draft
                                            </button>
                                        )}
                                        {canMarkReadyForReview && (
                                            <button
                                                onClick={() => handleStatusChange('ready_for_review')}
                                                disabled={contentSource.status !== 'draft'}
                                                className="editor-header-button editor-header-button-primary"
                                            >
                                                Ready for Review
                                            </button>
                                        )}
                                        {canMarkEthicsReview && (
                                            <button
                                                onClick={() => handleStatusChange('ethics_review')}
                                                disabled={contentSource.status !== 'ready_for_review'}
                                                className="editor-header-button editor-header-button-primary"
                                            >
                                                Send for Ethics Review
                                            </button>
                                        )}
                                        {canMarkReviewed && (
                                            <button
                                                onClick={() => handleStatusChange('ethics_reviewed')}
                                                disabled={contentSource.status !== 'ethics_review_in_process'}
                                                className="editor-header-button editor-header-button-success"
                                            >
                                                Mark Ethics Reviewed
                                            </button>
                                        )}
                                        {/* Show a message if no buttons are visible (for debugging) */}
                                        {!canReturnToDraft && !canMarkReadyForReview && !canMarkReviewed && isReviewMode && (
                                            <div style={{ 
                                                fontSize: '0.75rem', 
                                                color: '#666', 
                                                padding: '4px 8px',
                                                fontStyle: 'italic'
                                            }}>
                                                No status actions available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Editor Content */}
                            <div className="editor-main-content" style={{ position: 'relative' }}>
                                {showDiffView && isReviewMode ? (
                                    <DiffView
                                        originalContent={combinedOriginalContent}
                                        reviewedContent={combinedReviewedContent}
                                        viewMode="side-by-side"
                                        scrollToHeading={scrollToHeading}
                                    />
                                ) : (
                                    <TiptapEditor
                                        value={editingContent}
                                        onChange={handleContentChange}
                                        placeholder="Start writing your document here..."
                                        onEditorCreated={setEditorInstance}
                                        onFindReplace={() => setShowFindReplaceDialog(true)}
                                        editable={!isReviewMode}
                                        headings={headings}
                                        onVisibleSectionChange={handleVisibleSectionChange}
                                        onExtractPageNumbers={handleExtractPageNumbers}
                                    />
                                )}
                            </div>
                        </div>
                    );

                    const panelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' };
                    const defaultPanelSize = isSidebarCollapsed ? 95 : 80;

                    if (showPdfViewer) {
                        // When PDF viewer is open, wrap the inner PanelGroup in a Panel
                        // This Panel participates in the outer horizontal PanelGroup layout
                        // and contains the inner horizontal PanelGroup for editor/PDF split
                        return (
                            <Panel defaultSize={defaultPanelSize} minSize={50} style={panelStyle}>
                                <PanelGroup direction="horizontal" style={{ height: '100%', width: '100%' }}>
                                    {/* Editor Panel */}
                                    <Panel defaultSize={50} minSize={20} maxSize={80} style={panelStyle}>
                                        {editorContent}
                                    </Panel>
                                    
                                    {/* Resize Handle */}
                                    <PanelResizeHandle className="editor-sidebar-resizer" />
                                    
                                    {/* PDF Viewer Panel */}
                                    <Panel defaultSize={50} minSize={20} maxSize={80} style={panelStyle}>
                                        {pdfUrl && (
                                            <PdfViewer url={pdfUrl} className="editor-pdf-viewer" />
                                        )}
                                    </Panel>
                                </PanelGroup>
                            </Panel>
                        );
                    }

                    // When PDF viewer is closed, just return the editor in a Panel
                    return (
                        <Panel defaultSize={defaultPanelSize} minSize={50} style={panelStyle}>
                            {editorContent}
                        </Panel>
                    );
                })()}
            </PanelGroup>
            )}
            
            {/* Edit Content Source Modal */}
            {showEditModal && contentSource && (
                <ContentSourceModal
                    mode="edit"
                    initialData={{
                        title: contentSource.title,
                        source: contentSource.source,
                        ingest_source: contentSource.ingest_source,
                        filter_tags: !selectedSection ? localFilterTags : contentSource.filter_tags,
                        visibility: !selectedSection ? localVisibility : contentSource.visibility,
                        ref_visibility: !selectedSection ? localRefVisibility : contentSource.ref_visibility,
                        metadata: convertMetadataToSimpleFormat(contentSource.metadata || {}),
                        sarangpur_path: contentSource.sarangpur_path || null
                    }}
                    onClose={() => setShowEditModal(false)}
                    onSubmit={handleUpdateContentSource}
                />
            )}
            
            {/* Version Conflict Dialog */}
            <InfoDialog
                isOpen={isConflictDialogOpen}
                onClose={() => setIsConflictDialogOpen(false)}
                title="Version Conflict"
                message="This content has been modified by another user. Please refresh the page to see the latest version."
            />
            
            {/* Find and Replace Dialog */}
            <FindReplaceDialog
                editor={editorInstance}
                isOpen={showFindReplaceDialog}
                onClose={() => setShowFindReplaceDialog(false)}
            />
            
            {/* Info Dialog */}
            <InfoDialog
                isOpen={infoDialog.isOpen}
                onClose={() => setInfoDialog({ isOpen: false, title: '', message: '' })}
                title={infoDialog.title}
                message={infoDialog.message}
            />
        </div>
    );
};

export default ContentSourceEditor;
